import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { FiTrendingUp, FiTruck } from "react-icons/fi";
import { LuCalendarDays, LuDownload, LuEye } from "react-icons/lu";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import Breadcrumbs from "../../../components/Breadcrumbs";
import Button from "../../../components/button";
import CustomModal from "../../../components/CustomModal";
import Input from "../../../components/input";
import NewTableComponent from "../../../components/NewTableComponent";
import Select from "../../../components/Select";
import UserAvatar from "../../../components/UserAvatar";
import { apiService } from "../../../services/apiService";
import { getUserDropdown } from "../../../store/Slices/UserSlice";
import "../../../styles/Access.css";
import "../../../styles/Button.css";
import "../../../styles/FinancialReports.css";
import "../../../styles/Input.css";
import "../../../styles/Issue.css";
import "../../../styles/Services.css";
import "../../../styles/Table.css";
import "../../../styles/Trip.css";
import "../../../styles/TripCostEstimation.css";
import {
  AppMainModules,
  FinancePermission,
  FinanceSubModules,
  ReportsPermission,
} from "../../../utils/app-constant";
import { getCurrentUser, hasPermission } from "../../../utils/helper";
import { partValueIcon } from "../../../utils/svgs";
import { errorNotify, successNotify } from "../../../utils/toast";

const REPORT_FORMAT_OPTIONS = [
  { value: "pdf", label: "PDF" },
  { value: "excel", label: "Excel" },
  { value: "csv", label: "CSV" },
];

const ALL_TRIPS_OPTION = { value: "all-trips", label: "All Trips" };
const ALL_VEHICLES_OPTION = { value: "all-vehicles", label: "All Vehicles" };
const ALL_CUSTOMERS_OPTION = { value: "all-customers", label: "All Customers" };

const REPORT_TYPES = [
  {
    key: "trip-expense-report",
    title: "Trip Expense Report",
    chipLabel: "Trip Expense",
    badgeClass: "is_trip_expense",
    description:
      "Detailed breakdown of all operational costs incurred during a specific trip - aggregates expenses across all categories to provide a complete cost view.",
    icon: partValueIcon,
  },
  {
    key: "trip-pnl-report",
    title: "Trip PnL Report",
    chipLabel: "Trip PnL",
    badgeClass: "is_trip_pnl",
    description:
      "Profit and loss analysis for trips - combines invoice revenue against operational expenses to calculate net profitability.",
    icon: FiTrendingUp,
  },
  {
    key: "vehicle-pnl-report",
    title: "Vehicle PnL Report",
    chipLabel: "Vehicle PnL",
    badgeClass: "is_vehicle_pnl",
    description:
      "Profitability analysis at the vehicle level - identifies high-performing and cost-heavy vehicles across a date range/period.",
    icon: FiTruck,
  },
  {
    key: "monthly-fleet-pnl",
    title: "Monthly Fleet PnL",
    chipLabel: "Monthly Fleet",
    badgeClass: "is_monthly_fleet",
    description:
      "High-level fleet-wide financial overview for a given month - aggregates revenue and expenses across all vehicles and trips.",
    icon: LuCalendarDays,
  },
];

const REPORT_TYPE_META_BY_KEY = REPORT_TYPES.reduce((lookup, reportType) => {
  lookup[reportType.key] = reportType;
  return lookup;
}, {});

const REPORT_TYPE_META_BY_API_VALUE = {
  "trip-expense": REPORT_TYPE_META_BY_KEY["trip-expense-report"],
  "trip-pnl": REPORT_TYPE_META_BY_KEY["trip-pnl-report"],
  "vehicle-pnl": REPORT_TYPE_META_BY_KEY["vehicle-pnl-report"],
  "monthly-fleet-pnl": REPORT_TYPE_META_BY_KEY["monthly-fleet-pnl"],
  "monthly-trip-pnl": REPORT_TYPE_META_BY_KEY["monthly-fleet-pnl"],
};

const REPORT_TRIP_OPTIONS = [
  {
    value: "TRP-00041",
    label: "TRP-00041",
    vehicleId: "UKH-8556",
    driverId: "drv-sajid",
    customer: "-",
    branch: "-",
    amount: 32000,
    routeLabel: "Karachi -> Lahore",
  },
  {
    value: "TRP-00042",
    label: "TRP-00042",
    vehicleId: "LHR-8816",
    driverId: "drv-tariq",
    customer: "All Customers",
    branch: "-",
    amount: 5000,
    routeLabel: "Lahore -> Islamabad",
  },
  {
    value: "TRP-00043",
    label: "TRP-00043",
    vehicleId: "ICT-6652",
    driverId: "drv-faisal",
    customer: "Yasir & Co",
    branch: "-",
    amount: 2000,
    routeLabel: "Islamabad -> Faisalabad",
  },
  {
    value: "TRP-00044",
    label: "TRP-00044",
    vehicleId: "MLT-9997",
    driverId: "drv-irfan",
    customer: "Hunza Traders",
    branch: "RB Rawalpindi",
    amount: 800,
    routeLabel: "Multan -> Rawalpindi",
  },
];

const VEHICLE_OPTIONS = [
  { value: "UKH-8556", label: "UKH-8556" },
  { value: "LHR-8816", label: "LHR-8816" },
  { value: "ICT-6652", label: "ICT-6652" },
  { value: "MLT-9997", label: "MLT-9997" },
];

const TRIP_FILTER_OPTIONS = [
  ALL_TRIPS_OPTION,
  ...REPORT_TRIP_OPTIONS,
];

const VEHICLE_FILTER_OPTIONS = [
  ALL_VEHICLES_OPTION,
  ...VEHICLE_OPTIONS,
];

const CUSTOMER_OPTIONS = [
  ALL_CUSTOMERS_OPTION,
  { value: "customer-colgate", label: "Colgate Pakistan" },
  { value: "customer-yasir", label: "Yasir & Co" },
  { value: "customer-hunza", label: "Hunza Traders" },
];

const BRANCH_OPTIONS = [
  { value: "all-branches", label: "All Branches" },
  { value: "branch-rb-rawalpindi", label: "RB Rawalpindi" },
  { value: "branch-head-office", label: "Head Office" },
];

const INITIAL_FORM_VALUES = {
  tripId: "",
  exportFormat: "pdf",
  vehicleId: "",
  driverId: "",
  startDate: "",
  endDate: "",
  customerId: "",
  branchId: "",
  month: "",
};

const REPORT_FORM_DEFAULTS = {
  "trip-expense-report": {
    tripId: [],
    exportFormat: "pdf",
    vehicleId: [],
    driverId: [],
  },
  "trip-pnl-report": {
    tripId: [],
    vehicleId: [],
    customerId: [],
    exportFormat: "pdf",
  },
  "vehicle-pnl-report": {
    startDate: "",
    endDate: "",
    vehicleId: [],
    customerId: [],
    exportFormat: "pdf",
  },
  "monthly-fleet-pnl": {
    month: "",
    vehicleId: [],
    exportFormat: "pdf",
  },
};

const REPORT_FORM_FIELDS = {
  "trip-expense-report": [
    {
      name: "startDate",
      label: "Start Date",
      component: "input",
      inputType: "date",
      placeholder: "YYYY/MM/DD",
      col: 6,
    },
    {
      name: "endDate",
      label: "End Date",
      component: "input",
      inputType: "date",
      placeholder: "YYYY/MM/DD",
      col: 6,
    },
    {
      name: "tripId",
      label: "Trip ID",
      component: "select",
      options: [],
      placeholder: "Select Trip ID(s)",
      col: 6,
      isSearch: false,
      isMulti: true,
    },
    {
      name: "exportFormat",
      label: "Export Format",
      component: "select",
      options: REPORT_FORMAT_OPTIONS,
      placeholder: "Select Export Format",
      col: 6,
      isSearch: false,
    },
    {
      name: "vehicleId",
      label: "Vehicle",
      component: "select",
      options: [],
      placeholder: "Select Vehicle",
      col: 6,
      isSearch: false,
      isMulti: true,
    },
    {
      name: "driverId",
      label: "Driver",
      component: "select",
      options: [],
      placeholder: "Select Driver",
      col: 6,
      isSearch: false,
      isMulti: true,
    },
  ],
  "trip-pnl-report": [
    {
      name: "startDate",
      label: "Start Date",
      component: "input",
      inputType: "date",
      placeholder: "YYYY/MM/DD",
      col: 6,
    },
    {
      name: "endDate",
      label: "End Date",
      component: "input",
      inputType: "date",
      placeholder: "YYYY/MM/DD",
      col: 6,
    },
    {
      name: "tripId",
      label: "Trip ID",
      component: "select",
      options: TRIP_FILTER_OPTIONS,
      placeholder: "Select Trip ID",
      col: 6,
      isSearch: false,
      isMulti: true,
    },
    {
      name: "vehicleId",
      label: "Vehicle",
      component: "select",
      options: VEHICLE_FILTER_OPTIONS,
      placeholder: "Select Vehicle(s)",
      col: 6,
      isSearch: false,
      isMulti: true,
    },
    {
      name: "customerId",
      label: "Customers",
      component: "select",
      options: CUSTOMER_OPTIONS,
      placeholder: "Select Customer(s)",
      col: 6,
      isSearch: false,
      isMulti: true,
    },
    {
      name: "exportFormat",
      label: "Export Format",
      component: "select",
      options: REPORT_FORMAT_OPTIONS,
      placeholder: "Select Export Format",
      col: 6,
      isSearch: false,
    },
  ],
  "vehicle-pnl-report": [
    {
      name: "startDate",
      label: "Start Date",
      component: "input",
      inputType: "date",
      placeholder: "YYYY/MM/DD",
      col: 6,
    },
    {
      name: "endDate",
      label: "End Date",
      component: "input",
      inputType: "date",
      placeholder: "YYYY/MM/DD",
      col: 6,
    },
    {
      name: "vehicleId",
      label: "Vehicle",
      component: "select",
      options: VEHICLE_FILTER_OPTIONS,
      placeholder: "All Vehicles",
      col: 6,
      isSearch: false,
      isMulti: true,
    },
    {
      name: "customerId",
      label: "Customers",
      component: "select",
      options: CUSTOMER_OPTIONS,
      placeholder: "All Customers",
      col: 6,
      isSearch: false,
      isMulti: true,
    },
    {
      name: "exportFormat",
      label: "Export Format",
      component: "select",
      options: REPORT_FORMAT_OPTIONS,
      placeholder: "Select Export Format",
      col: 6,
      isSearch: false,
    },
  ],
  "monthly-fleet-pnl": [
    {
      name: "month",
      label: "Month",
      component: "input",
      inputType: "month",
      placeholder: "Select Month",
      col: 6,
    },
    {
      name: "vehicleId",
      label: "Vehicle",
      component: "select",
      options: VEHICLE_FILTER_OPTIONS,
      placeholder: "Select Vehicle(s)",
      col: 6,
      isSearch: false,
      isMulti: true,
    },
    {
      name: "exportFormat",
      label: "Export Format",
      component: "select",
      options: REPORT_FORMAT_OPTIONS,
      placeholder: "Select Export Format",
      col: 6,
      isSearch: false,
    },
  ],
};

const TRIP_EXPENSE_REPORT_VIEW_SEED = {
  "TRP-00041": {
    routeLabel: "Karachi -> Lahore",
    items: [
      {
        expenseId: "FLS-00040",
        source: "Fuel Log",
        tripId: "TRP-00041",
        category: "Fuel",
        amount: 18000,
      },
      {
        expenseId: "EXP-00041",
        source: "Driver Expense",
        tripId: "TRP-00041",
        category: "Food",
        amount: 5000,
      },
      {
        expenseId: "EXP-00042",
        source: "Driver Expense",
        tripId: "TRP-00041",
        category: "Accommodation",
        amount: 2000,
      },
      {
        expenseId: "MNT-00043",
        source: "Maintenance",
        tripId: "TRP-00041",
        category: "Tyre Check",
        amount: 800,
      },
      {
        expenseId: "POS-00044",
        source: "Inventory",
        tripId: "TRP-00041",
        category: "Brake Pads",
        amount: 6200,
      },
    ],
  },
  "TRP-00042": {
    routeLabel: "Lahore -> Islamabad",
    items: [
      {
        expenseId: "FLS-00045",
        source: "Fuel Log",
        tripId: "TRP-00042",
        category: "Fuel",
        amount: 3000,
      },
      {
        expenseId: "EXP-00046",
        source: "Driver Expense",
        tripId: "TRP-00042",
        category: "Meal Allowance",
        amount: 1200,
      },
      {
        expenseId: "POS-00047",
        source: "Inventory",
        tripId: "TRP-00042",
        category: "Consumables",
        amount: 800,
      },
    ],
  },
  "TRP-00043": {
    routeLabel: "Islamabad -> Faisalabad",
    items: [
      {
        expenseId: "FLS-00048",
        source: "Fuel Log",
        tripId: "TRP-00043",
        category: "Fuel",
        amount: 1500,
      },
      {
        expenseId: "EXP-00049",
        source: "Driver Expense",
        tripId: "TRP-00043",
        category: "Daily Allowance",
        amount: 500,
      },
    ],
  },
  "TRP-00044": {
    routeLabel: "Multan -> Rawalpindi",
    items: [
      {
        expenseId: "MNT-00050",
        source: "Maintenance",
        tripId: "TRP-00044",
        category: "Vehicle Inspection",
        amount: 800,
      },
    ],
  },
};

const safeArray = (value) => (Array.isArray(value) ? value : []);

const toFiniteNumber = (value, fallback = 0) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : fallback;
};

const formatAmount = (value) =>
  new Intl.NumberFormat("en-PK", {
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const normalizeText = (value) => String(value ?? "").trim().toLowerCase();

const pickOption = (options = [], value = "") =>
  safeArray(options).find(
    (option) => String(option?.value ?? "") === String(value ?? "")
  ) || null;

const getOptionLabel = (options = [], value = "", fallback = "-") =>
  pickOption(options, value)?.label || fallback;

const toValueArray = (value) => {
  if (Array.isArray(value)) return value.filter((item) => item !== "" && item !== null && item !== undefined);
  if (value === "" || value === null || value === undefined) return [];
  return [value];
};

const pickMultiOptions = (options = [], values = []) => {
  const selectedValues = new Set(
    toValueArray(values).map((value) => String(value ?? ""))
  );

  return safeArray(options).filter((option) =>
    selectedValues.has(String(option?.value ?? ""))
  );
};

const getOptionLabels = (options = [], values = [], fallback = "-") => {
  const labels = pickMultiOptions(options, values)
    .map((option) => String(option?.label || "").trim())
    .filter(Boolean);

  return labels.length ? labels.join(", ") : fallback;
};

const buildExclusiveAllOptions = (options = [], allOption = null, selectedValues = []) => {
  const normalizedSelectedValues = new Set(
    toValueArray(selectedValues).map((value) => String(value ?? ""))
  );
  const allOptionValue = String(allOption?.value ?? "");
  const hasAllSelected = allOptionValue && normalizedSelectedValues.has(allOptionValue);
  const baseOptions = safeArray(options).filter(
    (option) => String(option?.value ?? "") !== allOptionValue
  );

  if (!allOptionValue) {
    return baseOptions;
  }

  return [
    allOption,
    ...baseOptions.map((option) => ({
      ...option,
      isDisabled: hasAllSelected,
    })),
  ];
};

const normalizeExclusiveMultiValue = (value = [], allOptionValue = "") => {
  const normalizedValues = toValueArray(value);
  if (!allOptionValue) return normalizedValues;

  return normalizedValues.includes(allOptionValue) ? [allOptionValue] : normalizedValues;
};

const toPayloadIdValue = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : value;
};

const expandAllSelectionToPayloadIds = ({
  values = [],
  options = [],
  allOptionValue = "",
} = {}) => {
  const normalizedValues = normalizeExclusiveMultiValue(values, allOptionValue);
  const realOptionValues = safeArray(options)
    .map((option) => option?.value)
    .filter(
      (optionValue) =>
        optionValue !== undefined &&
        optionValue !== null &&
        optionValue !== "" &&
        String(optionValue) !== String(allOptionValue)
    );

  const selectedValues = normalizedValues.includes(allOptionValue)
    ? realOptionValues
    : normalizedValues.filter((value) => String(value) !== String(allOptionValue));

  return selectedValues.map(toPayloadIdValue);
};

const normalizeTripDropdownOption = (option = {}) => {
  const resolvedValue = option?.value ?? option?.id ?? "";
  const resolvedLabel =
    option?.label ??
    option?.trip_id ??
    option?.name ??
    (resolvedValue !== "" ? String(resolvedValue) : "");

  return {
    ...option,
    value: resolvedValue,
    label: resolvedLabel,
  };
};

const mergeTripOptions = (...groups) => {
  const lookup = new Map();

  groups.forEach((group) => {
    safeArray(group).forEach((option) => {
      const normalizedOption = normalizeTripDropdownOption(option);
      const normalizedValue = String(normalizedOption?.value ?? "").trim();

      if (!normalizedValue || lookup.has(normalizedValue)) return;
      lookup.set(normalizedValue, normalizedOption);
    });
  });

  return Array.from(lookup.values());
};

const normalizeVehicleDropdownOption = (vehicle = {}) => ({
  ...vehicle,
  value: vehicle?.id ?? vehicle?.value ?? "",
  label:
    vehicle?.registration_number ??
    vehicle?.label ??
    vehicle?.name ??
    (vehicle?.id !== undefined && vehicle?.id !== null ? String(vehicle.id) : ""),
});

const normalizeDriverDropdownOption = (driver = {}) => ({
  ...driver,
  value: driver?.id ?? driver?.value ?? "",
  label:
    driver?.username ??
    driver?.label ??
    driver?.name ??
    driver?.email ??
    (driver?.id !== undefined && driver?.id !== null ? String(driver.id) : ""),
  showIcon: true,
  image: driver?.image || "",
});

const normalizeCustomerDropdownOption = (customer = {}) => ({
  ...customer,
  value: customer?.id ?? customer?.value ?? "",
  label:
    customer?.name ??
    customer?.label ??
    (customer?.id !== undefined && customer?.id !== null ? String(customer.id) : ""),
});

const buildUserDropdownLookup = (users = []) =>
  safeArray(users).reduce((lookup, user) => {
    const resolvedId = Number(user?.value ?? user?.id ?? 0);
    if (!Number.isFinite(resolvedId) || resolvedId <= 0 || lookup.has(resolvedId)) {
      return lookup;
    }

    lookup.set(resolvedId, {
      label:
        user?.label ||
        user?.username ||
        user?.name ||
        user?.email ||
        `User ${resolvedId}`,
      image:
        user?.image ||
        user?.profile_image ||
        user?.profileImage ||
        "",
    });

    return lookup;
  }, new Map());

const toComparableDateValue = (value) => {
  if (!value) return Number.NaN;
  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? Number.NaN : parsedDate.getTime();
};

const formatHistoryDateDisplay = (value = "") => {
  const [year, month, day] = String(value || "").split("-");
  if (!year || !month || !day) return String(value || "");
  return `${year}-${day}-${month}`;
};

const formatLocalDateValue = (value = "") => {
  if (!value) return "";

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return String(value || "").split("T")[0] || String(value || "");
  }

  const year = parsedDate.getFullYear();
  const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
  const day = String(parsedDate.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatMonthLabel = (value = "") => {
  if (!value) return "-";
  const parsedDate = new Date(`${value}-01`);
  if (Number.isNaN(parsedDate.getTime())) return String(value || "");

  const monthLabel = parsedDate.toLocaleDateString("en-US", {
    month: "long",
  });
  return `${monthLabel}-${parsedDate.getFullYear()}`;
};

const normalizeReportTypeToken = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/_/g, "-")
    .replace(/\s+/g, "-");

const resolveReportTypeMeta = (reportType = "") => {
  const normalizedType = normalizeReportTypeToken(reportType);

  if (REPORT_TYPE_META_BY_API_VALUE[normalizedType]) {
    return REPORT_TYPE_META_BY_API_VALUE[normalizedType];
  }

  if (REPORT_TYPE_META_BY_KEY[normalizedType]) {
    return REPORT_TYPE_META_BY_KEY[normalizedType];
  }

  const matchedReportType = REPORT_TYPES.find(
    (item) => normalizeReportTypeToken(item.title) === normalizedType
  );

  return (
    matchedReportType || {
      key: normalizedType || "financial-report",
      title: String(reportType || "Financial Report")
        .replace(/[_-]+/g, " ")
        .replace(/\b\w/g, (character) => character.toUpperCase()),
    }
  );
};

const resolveMonthlyHistoryLabel = (report = {}) => {
  if (report?.month_name) {
    return report.month_name;
  }

  const monthValue = String(report?.month ?? "").trim();
  const yearValue = String(report?.year ?? "").trim();

  if (!monthValue) return "";

  const normalizedMonthValue = monthValue.length === 1 ? `0${monthValue}` : monthValue;
  return formatMonthLabel(`${yearValue || new Date().getFullYear()}-${normalizedMonthValue}`);
};

const resolveFinancialReportAmount = (report = {}, reportTypeKey = "") => {
  if (reportTypeKey === "trip-expense-report") {
    return toFiniteNumber(report?.total_expense);
  }

  if (reportTypeKey === "trip-pnl-report") {
    return toFiniteNumber(report?.net_profit, toFiniteNumber(report?.total_revenue));
  }

  if (reportTypeKey === "vehicle-pnl-report" || reportTypeKey === "monthly-fleet-pnl") {
    return toFiniteNumber(report?.net_profit, toFiniteNumber(report?.total_revenue));
  }

  return toFiniteNumber(
    report?.net_profit,
    toFiniteNumber(report?.total_expense, toFiniteNumber(report?.total_revenue))
  );
};

const resolveFinancialReportGeneratedBy = (
  report = {},
  currentUser = null,
  userDropdownLookup = new Map()
) => {
  const creatorId = Number(report?.created_by);
  const currentUserId = Number(currentUser?.id);
  const dropdownUser = Number.isFinite(creatorId)
    ? userDropdownLookup.get(creatorId)
    : null;

  if (
    Number.isFinite(creatorId) &&
    Number.isFinite(currentUserId) &&
    creatorId > 0 &&
    creatorId === currentUserId
  ) {
    return currentUser?.username || currentUser?.name || currentUser?.email || `User ${creatorId}`;
  }

  return (
    dropdownUser?.label ||
    report?.created_by_name ||
    report?.created_by_username ||
    report?.created_by_email ||
    (creatorId > 0 ? `User ${creatorId}` : "-")
  );
};

const resolveFinancialReportGeneratedByImage = (
  report = {},
  currentUser = null,
  userDropdownLookup = new Map()
) => {
  const creatorId = Number(report?.created_by);
  const currentUserId = Number(currentUser?.id);
  const dropdownUser = Number.isFinite(creatorId)
    ? userDropdownLookup.get(creatorId)
    : null;

  if (
    Number.isFinite(creatorId) &&
    Number.isFinite(currentUserId) &&
    creatorId > 0 &&
    creatorId === currentUserId
  ) {
    return (
      currentUser?.image ||
      currentUser?.profile_image ||
      currentUser?.profileImage ||
      currentUser?.details?.image ||
      ""
    );
  }

  return dropdownUser?.image || report?.created_by_image || report?.created_by_avatar || "";
};

const buildFinancialReportHistoryRow = (
  report = {},
  currentUser = null,
  userDropdownLookup = new Map()
) => {
  const reportTypeMeta = resolveReportTypeMeta(report?.report_type);
  const generatedOnValue = formatLocalDateValue(report?.created_at || report?.updated_at);
  const startDate = formatLocalDateValue(report?.start_date);
  const endDate = formatLocalDateValue(report?.end_date);
  const monthLabel = resolveMonthlyHistoryLabel(report);
  const reportId =
    report?.financial_reports_id ||
    (report?.id !== undefined && report?.id !== null ? `FR-${report.id}` : "-");

  return {
    id: report?.id ?? reportId,
    reportId,
    reportTypeKey: reportTypeMeta?.key || "financial-report",
    reportType: reportTypeMeta?.title || "Financial Report",
    generatedOnValue,
    generatedOnDisplay: formatHistoryDateDisplay(generatedOnValue),
    generatedBy: resolveFinancialReportGeneratedBy(report, currentUser, userDropdownLookup),
    generatedByImage: resolveFinancialReportGeneratedByImage(
      report,
      currentUser,
      userDropdownLookup
    ),
    generatedById: report?.created_by ?? "",
    exportFormat: String(report?.export_format || "pdf").toLowerCase(),
    startDate,
    endDate,
    monthName: monthLabel || "-",
    dateRangeLabel:
      monthLabel || startDate || endDate
        ? monthLabel || `${startDate || "-"} to ${endDate || "-"}`
        : "-",
    amount: resolveFinancialReportAmount(report, reportTypeMeta?.key),
    totalRevenue: toFiniteNumber(report?.total_revenue),
    totalExpense: toFiniteNumber(report?.total_expense),
    netProfit: toFiniteNumber(report?.net_profit),
    profitMargin: toFiniteNumber(report?.profit_margin),
    financialReportId: report?.financial_reports_id || "",
    tripId: "-",
    vehicleId: "-",
    customer: "-",
    branch: "-",
    hasDetailedPreview: false,
  };
};

const renderFinancialReportTypeChip = (row = {}) => {
  const reportTypeMeta = resolveReportTypeMeta(row?.reportTypeKey || row?.reportType);
  const ReportTypeIcon = reportTypeMeta?.icon;
  const chipLabel = reportTypeMeta?.chipLabel || reportTypeMeta?.title || row?.reportType || "-";
  const chipClassName = reportTypeMeta?.badgeClass || "is_default";

  return (
    <span className={`financial_report_type_chip ${chipClassName}`}>
      <span className="financial_report_type_chip_icon">
        {typeof ReportTypeIcon === "function" ? <ReportTypeIcon /> : ReportTypeIcon}
      </span>
      <span>{chipLabel}</span>
    </span>
  );
};

const getReportTypeDefaults = (reportTypeKey = REPORT_TYPES[0].key) => ({
  ...INITIAL_FORM_VALUES,
  ...(REPORT_FORM_DEFAULTS[reportTypeKey] || {}),
});

const getMatchingTripsForReport = (
  reportTypeKey = "",
  values = {},
  tripOptions = REPORT_TRIP_OPTIONS,
  filterOptions = {}
) => {
  const selectedBranchLabel = getOptionLabel(BRANCH_OPTIONS, values.branchId, "");
  const selectedTripIds = new Set(
    toValueArray(values.tripId)
      .map((value) => String(value ?? ""))
      .filter((value) => value && value !== ALL_TRIPS_OPTION.value)
  );
  const hasAllTripsSelected = toValueArray(values.tripId).includes(ALL_TRIPS_OPTION.value);
  const selectedVehicleLabels = pickMultiOptions(
    filterOptions.vehicleOptions || VEHICLE_FILTER_OPTIONS,
    values.vehicleId
  )
    .map((option) => normalizeText(option?.label))
    .filter((label) => Boolean(label) && label !== normalizeText(ALL_VEHICLES_OPTION.label));
  const selectedCustomerLabels = pickMultiOptions(
    filterOptions.customerOptions || CUSTOMER_OPTIONS,
    values.customerId
  )
    .map((option) => normalizeText(option?.label))
    .filter((label) => Boolean(label) && label !== normalizeText(ALL_CUSTOMERS_OPTION.label));

  return safeArray(tripOptions).filter((trip) => {
    if (reportTypeKey === "trip-expense-report") {
      return selectedTripIds.has(String(trip.value ?? ""));
    }

    if (
      reportTypeKey === "trip-pnl-report" &&
      !hasAllTripsSelected &&
      selectedTripIds.size &&
      !selectedTripIds.has(String(trip.value ?? ""))
    ) {
      return false;
    }

    if (
      selectedVehicleLabels.length &&
      !selectedVehicleLabels.includes(normalizeText(trip.vehicleId))
    ) {
      return false;
    }

    if (
      selectedCustomerLabels.length &&
      !selectedCustomerLabels.includes(normalizeText(trip.customer))
    ) {
      return false;
    }

    if (
      reportTypeKey === "monthly-fleet-pnl" &&
      values.branchId &&
      values.branchId !== "all-branches" &&
      normalizeText(trip.branch) !== normalizeText(selectedBranchLabel)
    ) {
      return false;
    }

    return true;
  });
};

const matchesSearchTerm = (row = {}, searchText = "") => {
  const normalizedSearchText = normalizeText(searchText);
  if (!normalizedSearchText) return true;

  const searchableValues = [
    row?.reportId,
    row?.reportType,
    row?.generatedOnDisplay,
    row?.generatedBy,
    row?.dateRangeLabel,
    row?.exportFormat,
    row?.monthName,
    row?.totalRevenue,
    row?.totalExpense,
    row?.netProfit,
    row?.profitMargin,
    row?.amount,
  ];

  return searchableValues.some((value) =>
    normalizeText(value).includes(normalizedSearchText)
  );
};

const matchesFilter = (rowValue, filterMeta = {}) => {
  const filterType = filterMeta?.filter_type;
  const filterValue = filterMeta?.value;
  const rowValues = Array.isArray(rowValue) ? rowValue : [rowValue];
  const normalizedRowValues = rowValues.map((value) => normalizeText(value));

  if (!filterType || filterValue === undefined || filterValue === null || filterValue === "") {
    return true;
  }

  if (filterType === "one_of") {
    const filterValues = safeArray(filterValue).map((value) => normalizeText(value));
    return normalizedRowValues.some((value) => filterValues.includes(value));
  }

  if (filterType === "contains") {
    return normalizedRowValues.some((value) => value.includes(normalizeText(filterValue)));
  }

  if (filterType === "equals" || filterType === "equal_to") {
    const normalizedFilterValue = normalizeText(filterValue);
    return normalizedRowValues.some((value) => value === normalizedFilterValue);
  }

  const comparableRowValue = rowValues[0];
  const rowDateValue = toComparableDateValue(comparableRowValue);
  const filterDateValue = toComparableDateValue(filterValue);

  if (!Number.isNaN(rowDateValue) && !Number.isNaN(filterDateValue)) {
    if (filterType === "less_than") return rowDateValue < filterDateValue;
    if (filterType === "greater_than") return rowDateValue > filterDateValue;
    if (filterType === "less_than_equal_to") return rowDateValue <= filterDateValue;
    if (filterType === "greater_than_equal_to") return rowDateValue >= filterDateValue;
  }

  const rowNumberValue = Number(comparableRowValue);
  const filterNumberValue = Number(filterValue);

  if (Number.isFinite(rowNumberValue) && Number.isFinite(filterNumberValue)) {
    if (filterType === "less_than") return rowNumberValue < filterNumberValue;
    if (filterType === "greater_than") return rowNumberValue > filterNumberValue;
    if (filterType === "less_than_equal_to") return rowNumberValue <= filterNumberValue;
    if (filterType === "greater_than_equal_to") return rowNumberValue >= filterNumberValue;
  }

  return true;
};

const buildReportPreviewHtml = (row = {}) => `
  <!doctype html>
  <html>
    <head>
      <title>${row.reportType || "Financial Report"}</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 24px; color: #1f2937; }
        h1 { font-size: 24px; margin-bottom: 12px; }
        p { margin: 0 0 8px; }
        .meta { margin-bottom: 20px; color: #4b5563; }
        table { width: 100%; border-collapse: collapse; margin-top: 16px; }
        th, td { border: 1px solid #dbe3f1; padding: 10px 12px; text-align: left; }
        th { background: #f5f7fb; }
      </style>
    </head>
    <body>
      <h1>${row.reportType || "Financial Report"}</h1>
      <div class="meta">
        <p>Report ID: ${row.reportId || "-"}</p>
        <p>Generated On: ${row.generatedOnDisplay || row.generatedOnValue || "-"}</p>
        <p>Generated By: ${row.generatedBy || "-"}</p>
        <p>Export Format: ${String(row.exportFormat || "-").toUpperCase()}</p>
      </div>
      <table>
        <tr><th>Date Range</th><td>${row.dateRangeLabel || `${row.startDate || "-"} to ${row.endDate || "-"}`}</td></tr>
        <tr><th>Total Revenue (PKR)</th><td>${formatAmount(row.totalRevenue)}</td></tr>
        <tr><th>Total Expense (PKR)</th><td>${formatAmount(row.totalExpense)}</td></tr>
        <tr><th>Net Profit (PKR)</th><td>${formatAmount(row.netProfit)}</td></tr>
        <tr><th>Profit Margin</th><td>${toFiniteNumber(row.profitMargin).toFixed(2)}%</td></tr>
        <tr><th>Amount (PKR)</th><td>${formatAmount(row.amount)}</td></tr>
      </table>
    </body>
  </html>
`;

const requiredStar = <span className="required_star">*</span>;

const renderFieldLabel = (label, required = true) => (
  <>
    {label}
    {required ? requiredStar : null}
  </>
);

const isTripExpenseReport = (reportKeyOrTitle = "") => {
  const normalizedValue = normalizeText(reportKeyOrTitle);
  return (
    normalizedValue === "trip-expense-report" ||
    normalizedValue === normalizeText(REPORT_TYPES[0].title)
  );
};

const buildFallbackTripExpenseItems = (tripId = "-", amount = 0) => [
  {
    expenseId: `EXP-${String(tripId || "00000").replace(/[^0-9]/g, "").padStart(5, "0").slice(-5)}`,
    source: "Expense Ledger",
    tripId: tripId || "-",
    category: "Operational Cost",
    amount: Number(amount || 0),
  },
];

const buildTripExpenseDetailItemsFromApi = (expenses = []) =>
  safeArray(expenses).map((expense) => ({
    expenseId:
      expense?.id !== undefined && expense?.id !== null
        ? `EXP-${String(expense.id).padStart(5, "0")}`
        : "-",
    source: "Expense Ledger",
    tripId:
      expense?.trip_id !== undefined && expense?.trip_id !== null
        ? String(expense.trip_id)
        : "-",
    category:
      expense?.expense_category?.title ||
      expense?.trip_expense_category ||
      "Uncategorized",
    amount: toFiniteNumber(expense?.amount),
  }));

const humanizeExpenseCategory = (value = "") =>
  String(value || "")
    .trim()
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());

const resolveExpenseSourceType = (expense = {}) => {
  const normalizedCategory = normalizeText(
    expense?.expense_category?.slug ||
    expense?.expense_category?.title ||
    expense?.trip_expense_category
  );

  if (
    [
      "food",
      "food beverage",
      "trip incentive",
      "loading unloading",
      "other works",
    ].includes(normalizedCategory)
  ) {
    return "driver";
  }

  if (normalizedCategory === "fuel") {
    return "fuel";
  }

  return "maintenance";
};

const buildTripExpenseReportViewDataFromApi = (report = {}) => {
  const items = buildTripExpenseDetailItemsFromApi(report?.expenses);
  const tripIds = safeArray(report?.tripIds)
    .map((tripId) => String(tripId ?? "").trim())
    .filter(Boolean);
  const vehicleIds = safeArray(report?.vehicleIds)
    .map((vehicleId) => String(vehicleId ?? "").trim())
    .filter(Boolean);
  const totalFromItems = items.reduce((sum, item) => sum + toFiniteNumber(item.amount), 0);

  return {
    tripId:
      tripIds.length === 1
        ? tripIds[0]
        : tripIds.length > 1
          ? `${tripIds.length} Trips`
          : "-",
    routeLabel:
      vehicleIds.length === 1
        ? `Vehicle ${vehicleIds[0]}`
        : vehicleIds.length > 1
          ? `${vehicleIds.length} Vehicles`
          : "Expense Detail",
    items,
    totalAmount: toFiniteNumber(report?.total_expense, totalFromItems),
  };
};

const buildTripPnlReportViewDataFromApi = (report = {}) => {
  const settlements = safeArray(report?.settlement);
  const totalRevenue = toFiniteNumber(report?.total_revenue);
  const totalExpenses = toFiniteNumber(report?.total_expense);
  const netProfit = toFiniteNumber(report?.net_profit);
  const profitMargin = toFiniteNumber(report?.profit_margin);

  const revenueItems = settlements.map((settlement, index) => {
    const tripId = settlement?.trip_id !== undefined && settlement?.trip_id !== null
      ? String(settlement.trip_id)
      : "-";
    const vehicleId = settlement?.vehicle_id !== undefined && settlement?.vehicle_id !== null
      ? String(settlement.vehicle_id)
      : "-";
    const customerId = settlement?.customer_id !== undefined && settlement?.customer_id !== null
      ? String(settlement.customer_id)
      : "-";
    const tripRevenue =
      toFiniteNumber(settlement?.trip_net_profit) +
      toFiniteNumber(settlement?.trip_net_expenses);

    return {
      expenseId: `REV-${index + 1}`,
      tripId,
      category: `Trip ${tripId} Revenue`,
      source: `Vehicle ${vehicleId} | Customer ${customerId}`,
      amount: tripRevenue,
      sourceType: "invoice",
    };
  }).filter((item) => item.amount > 0);

  const derivedRevenueTotal = revenueItems.reduce((sum, item) => sum + toFiniteNumber(item.amount), 0);
  const revenueDifference = Number((totalRevenue - derivedRevenueTotal).toFixed(2));

  if (!revenueItems.length && totalRevenue > 0) {
    revenueItems.push({
      expenseId: "REV-TOTAL",
      tripId: "-",
      category: "Total Revenue",
      source: "Financial Report",
      amount: totalRevenue,
      sourceType: "invoice",
    });
  }

  if (Math.abs(revenueDifference) > 0.009) {
    revenueItems.push({
      expenseId: "REV-ADJ",
      tripId: "-",
      category: "Revenue Adjustment",
      source: "Financial Report",
      amount: revenueDifference,
      sourceType: "invoice",
    });
  }

  const aggregateExpenseTotals = {
    fuel: 0,
    shortage: 0,
    insurance: 0,
    operational: 0,
  };

  const expenseItems = settlements.flatMap((settlement, settlementIndex) => {
    const tripId = settlement?.trip_id !== undefined && settlement?.trip_id !== null
      ? String(settlement.trip_id)
      : "-";
    const vehicleId = settlement?.vehicle_id !== undefined && settlement?.vehicle_id !== null
      ? String(settlement.vehicle_id)
      : "-";
    const fixCharges = safeArray(settlement?.trip_data?.fix_charges);
    const fixChargeNames = fixCharges
      .map((charge) => humanizeExpenseCategory(charge?.type))
      .filter(Boolean);
    const operationalLabel = fixChargeNames.length
      ? `Operational Expenses - ${fixChargeNames.join(", ")}`
      : "Operational Expenses";

    const breakdown = [
      {
        expenseId: `EXP-${settlementIndex + 1}-FUEL`,
        tripId,
        category: "Fuel Expense",
        source: `Trip ${tripId} | Vehicle ${vehicleId}`,
        amount: toFiniteNumber(settlement?.total_fuel_expense),
        sourceType: "fuel",
        aggregateKey: "fuel",
      },
      {
        expenseId: `EXP-${settlementIndex + 1}-SHORTAGE`,
        tripId,
        category: "Shortage Expense",
        source: `Trip ${tripId} | Vehicle ${vehicleId}`,
        amount: toFiniteNumber(settlement?.total_shortage_expense),
        sourceType: "maintenance",
        aggregateKey: "shortage",
      },
      {
        expenseId: `EXP-${settlementIndex + 1}-INSURANCE`,
        tripId,
        category: "Insurance Expense",
        source: `Trip ${tripId} | Vehicle ${vehicleId}`,
        amount: toFiniteNumber(settlement?.total_insurance_expense),
        sourceType: "maintenance",
        aggregateKey: "insurance",
      },
      {
        expenseId: `EXP-${settlementIndex + 1}-OPS`,
        tripId,
        category: operationalLabel,
        source: `Trip ${tripId} | Vehicle ${vehicleId}`,
        amount: toFiniteNumber(settlement?.total_operational_expenses),
        sourceType: "driver",
        aggregateKey: "operational",
      },
    ];

    return breakdown.filter((item) => {
      aggregateExpenseTotals[item.aggregateKey] += toFiniteNumber(item.amount);
      return item.amount > 0;
    });
  });

  if (!expenseItems.length && totalExpenses > 0) {
    expenseItems.push({
      expenseId: "EXP-TOTAL",
      tripId: "-",
      category: "Total Expenses",
      source: "Financial Report",
      amount: totalExpenses,
      sourceType: "maintenance",
    });
  }

  const uniqueTripIds = [...new Set(safeArray(report?.tripIds).map((value) => String(value ?? "").trim()).filter(Boolean))];
  const uniqueVehicleIds = [...new Set(safeArray(report?.vehicleIds).map((value) => String(value ?? "").trim()).filter(Boolean))];
  const uniqueCustomerIds = [...new Set(safeArray(report?.customerIds).map((value) => String(value ?? "").trim()).filter(Boolean))];

  const summaryRows = [
    { label: "Total Revenue (PKR)", value: totalRevenue },
    { label: "Total Expenses (PKR)", value: totalExpenses },
    { label: "Net Profit (PKR)", value: netProfit },
    { label: "Fuel Expense (PKR)", value: aggregateExpenseTotals.fuel },
    { label: "Shortage Expense (PKR)", value: aggregateExpenseTotals.shortage },
    { label: "Insurance Expense (PKR)", value: aggregateExpenseTotals.insurance },
    { label: "Operational Expense (PKR)", value: aggregateExpenseTotals.operational },
    { label: "Profit Margin", displayValue: `${profitMargin.toFixed(2)}%` },
    { label: "Trips Covered", displayValue: String(uniqueTripIds.length) },
    { label: "Vehicles Covered", displayValue: String(uniqueVehicleIds.length) },
    { label: "Customers Covered", displayValue: String(uniqueCustomerIds.length) },
  ];

  return {
    tripId:
      uniqueTripIds.length === 1
        ? uniqueTripIds[0]
        : uniqueTripIds.length > 1
          ? `${uniqueTripIds.length} Trips`
          : "-",
    routeLabel:
      uniqueVehicleIds.length === 1
        ? `Vehicle ${uniqueVehicleIds[0]}`
        : uniqueVehicleIds.length > 1
          ? `${uniqueVehicleIds.length} Vehicles`
          : "PnL Detail",
    totalRevenue,
    totalExpenses,
    netProfit,
    revenueItems,
    expenseItems,
    summaryRows,
    expenseTabLabel: "Expense Breakdown",
  };
};

const buildVehiclePnLReportViewDataFromApi = (report = {}) => {
  const settlements = safeArray(report?.settlement);
  const totalFleetRevenue = toFiniteNumber(report?.total_revenue);
  const totalFleetExpenses = toFiniteNumber(report?.total_expense);
  const totalFleetProfit = toFiniteNumber(report?.net_profit);

  const costSummary = settlements.reduce(
    (summary, settlement) => ({
      revenue:
        summary.revenue +
        toFiniteNumber(settlement?.trip_net_profit) +
        toFiniteNumber(settlement?.trip_net_expenses),
      fuel: summary.fuel + toFiniteNumber(settlement?.total_fuel_expense),
      maintenance:
        summary.maintenance + toFiniteNumber(settlement?.total_operational_expenses),
      parts: summary.parts + toFiniteNumber(settlement?.total_shortage_expense),
      driverExpense:
        summary.driverExpense + toFiniteNumber(settlement?.total_insurance_expense),
    }),
    {
      revenue: 0,
      fuel: 0,
      maintenance: 0,
      parts: 0,
      driverExpense: 0,
    }
  );

  const groupedVehicles = settlements.reduce((lookup, settlement) => {
    const vehicleId =
      settlement?.vehicle_id !== undefined && settlement?.vehicle_id !== null
        ? String(settlement.vehicle_id)
        : "-";

    const nextRevenue =
      toFiniteNumber(settlement?.trip_net_profit) +
      toFiniteNumber(settlement?.trip_net_expenses);
    const nextExpenses = toFiniteNumber(settlement?.trip_net_expenses);
    const nextProfit = toFiniteNumber(settlement?.trip_net_profit);

    if (!lookup[vehicleId]) {
      lookup[vehicleId] = {
        vehicle: vehicleId,
        type:
          settlement?.vehicle_type_id !== undefined && settlement?.vehicle_type_id !== null
            ? `Vehicle Type ${settlement.vehicle_type_id}`
            : "-",
        tripIds: new Set(),
        revenue: 0,
        expenses: 0,
        profit: 0,
      };
    }

    lookup[vehicleId].tripIds.add(
      settlement?.trip_id !== undefined && settlement?.trip_id !== null
        ? String(settlement.trip_id)
        : "-"
    );
    lookup[vehicleId].revenue += nextRevenue;
    lookup[vehicleId].expenses += nextExpenses;
    lookup[vehicleId].profit += nextProfit;

    return lookup;
  }, {});

  const vehicles = Object.values(groupedVehicles).map((vehicle) => {
    const margin =
      vehicle.revenue > 0
        ? Number(((vehicle.profit / vehicle.revenue) * 100).toFixed(2))
        : 0;

    return {
      vehicle: vehicle.vehicle,
      type: vehicle.type,
      trips: vehicle.tripIds.size,
      revenue: vehicle.revenue,
      expenses: vehicle.expenses,
      profit: vehicle.profit,
      margin,
    };
  });

  if (!vehicles.length && totalFleetRevenue > 0) {
    vehicles.push({
      vehicle:
        safeArray(report?.vehicleIds).length === 1
          ? String(report.vehicleIds[0])
          : `${safeArray(report?.vehicleIds).filter((value) => value !== null && value !== undefined).length || 0} Vehicles`,
      type: "-",
      trips: settlements.length,
      revenue: totalFleetRevenue,
      expenses: totalFleetExpenses,
      profit: totalFleetProfit,
      margin: totalFleetRevenue > 0
        ? Number(((totalFleetProfit / totalFleetRevenue) * 100).toFixed(2))
        : 0,
    });
  }

  return {
    totalFleetRevenue,
    totalFleetExpenses,
    totalFleetProfit,
    vehicles,
    costSummary: {
      revenue: totalFleetRevenue || costSummary.revenue,
      fuel: costSummary.fuel,
      maintenance: costSummary.maintenance,
      parts: costSummary.parts,
      driverExpense: costSummary.driverExpense,
    },
  };
};

const getTripExpenseReportViewData = (row = {}) => {
  const resolvedTripId = row?.tripId && row.tripId !== "-" ? row.tripId : "TRP-00041";
  const seededReport = TRIP_EXPENSE_REPORT_VIEW_SEED[resolvedTripId];
  const expenseItems = safeArray(seededReport?.items).length
    ? seededReport.items
    : buildFallbackTripExpenseItems(resolvedTripId, row.amount);
  const totalAmount =
    expenseItems.reduce((sum, item) => sum + Number(item.amount || 0), 0) ||
    Number(row.amount || 0);

  return {
    tripId: resolvedTripId,
    routeLabel: seededReport?.routeLabel || row.routeLabel || "-",
    items: expenseItems,
    totalAmount,
  };
};

const TRIP_PNL_REPORT_VIEW_SEED = {
  "TRP-00041": {
    totalRevenue: 84500,
    totalExpenses: 33600,
    netProfit: 50900,
    revenueItems: [
      { category: "Base Trip Charge", source: "Invoice Module", amount: 80000, sourceType: "invoice" },
      { category: "Fuel Surcharge", source: "Invoice Module", amount: 3000, sourceType: "invoice" },
      { category: "Toll Reimbursement", source: "Invoice Module", amount: 1500, sourceType: "invoice" }
    ],
    expenseItems: [
      { category: "Fuel", source: "Fuel Module", amount: 28000, sourceType: "fuel" },
      { category: "Driver Expenses", source: "Driver Expense", amount: 3600, sourceType: "driver" },
      { category: "Maintenance", source: "Maintenance", amount: 2000, sourceType: "maintenance" }
    ],
    costSummary: {
      baseTripCharge: 80000,
      fuelSurcharge: 3000,
      tollReimbursement: 1500,
      fuel: 28000,
      driverExpense: 3600,
      maintenance: 3600
    }
  },
  "TRP-00042": {
    totalRevenue: 65000,
    totalExpenses: 28400,
    netProfit: 36600,
    revenueItems: [
      { category: "Base Trip Charge", source: "Invoice Module", amount: 60000, sourceType: "invoice" },
      { category: "Fuel Surcharge", source: "Invoice Module", amount: 4000, sourceType: "invoice" },
      { category: "Loading Charges", source: "Invoice Module", amount: 1000, sourceType: "invoice" }
    ],
    expenseItems: [
      { category: "Fuel", source: "Fuel Module", amount: 22000, sourceType: "fuel" },
      { category: "Driver Expenses", source: "Driver Expense", amount: 4400, sourceType: "driver" },
      { category: "Toll Tax", source: "Expense Ledger", amount: 2000, sourceType: "maintenance" }
    ],
    costSummary: {
      baseTripCharge: 60000,
      fuelSurcharge: 4000,
      tollReimbursement: 1000,
      fuel: 22000,
      driverExpense: 4400,
      maintenance: 2000
    }
  },
  "TRP-00043": {
    totalRevenue: 45000,
    totalExpenses: 18500,
    netProfit: 26500,
    revenueItems: [
      { category: "Base Trip Charge", source: "Invoice Module", amount: 42000, sourceType: "invoice" },
      { category: "Unloading Charges", source: "Invoice Module", amount: 3000, sourceType: "invoice" }
    ],
    expenseItems: [
      { category: "Fuel", source: "Fuel Module", amount: 15000, sourceType: "fuel" },
      { category: "Food Allowance", source: "Driver Expense", amount: 2500, sourceType: "driver" },
      { category: "Miscellaneous", source: "Expense Ledger", amount: 1000, sourceType: "maintenance" }
    ],
    costSummary: {
      baseTripCharge: 42000,
      fuelSurcharge: 0,
      tollReimbursement: 3000,
      fuel: 15000,
      driverExpense: 2500,
      maintenance: 1000
    }
  },
  "TRP-00044": {
    totalRevenue: 110000,
    totalExpenses: 45000,
    netProfit: 65000,
    revenueItems: [
      { category: "Base Trip Charge", source: "Invoice Module", amount: 100000, sourceType: "invoice" },
      { category: "Fuel Surcharge", source: "Invoice Module", amount: 8000, sourceType: "invoice" },
      { category: "Toll Reimbursement", source: "Invoice Module", amount: 2000, sourceType: "invoice" }
    ],
    expenseItems: [
      { category: "Fuel", source: "Fuel Module", amount: 35000, sourceType: "fuel" },
      { category: "Driver Expenses", source: "Driver Expense", amount: 6000, sourceType: "driver" },
      { category: "Spare Parts", source: "Inventory", amount: 4000, sourceType: "maintenance" }
    ],
    costSummary: {
      baseTripCharge: 100000,
      fuelSurcharge: 8000,
      tollReimbursement: 2000,
      fuel: 35000,
      driverExpense: 6000,
      maintenance: 4000
    }
  }
};

const getTripPnLReportViewData = (row = {}) => {
  if (row?.apiTripPnlViewData) {
    return row.apiTripPnlViewData;
  }

  const resolvedTripId = row?.tripId && row.tripId !== "-" && row.tripId !== "all-trips" ? row.tripId : "TRP-00041";
  const seededReport = TRIP_PNL_REPORT_VIEW_SEED[resolvedTripId];
  
  if (seededReport) {
    return { ...seededReport, tripId: resolvedTripId };
  }

  const amount = Number(row.amount || 0);
  const totalRevenue = amount > 0 ? amount : 50000;
  
  // Distribute revenue
  const baseCharge = Math.floor(totalRevenue * 0.9);
  const fuelSurcharge = Math.floor(totalRevenue * 0.07);
  const tollReimb = totalRevenue - baseCharge - fuelSurcharge;

  const totalExpenses = Math.floor(totalRevenue * 0.4);
  
  // Distribute expenses
  const fuelExp = Math.floor(totalExpenses * 0.7);
  const driverExp = Math.floor(totalExpenses * 0.2);
  const maintExp = totalExpenses - fuelExp - driverExp;

  const netProfit = totalRevenue - totalExpenses;

  return {
    tripId: resolvedTripId,
    totalRevenue,
    totalExpenses,
    netProfit,
    revenueItems: [
      { category: "Base Trip Charge", source: "Invoice Module", amount: baseCharge, sourceType: "invoice" },
      { category: "Fuel Surcharge", source: "Invoice Module", amount: fuelSurcharge, sourceType: "invoice" },
      { category: "Toll Reimbursement", source: "Invoice Module", amount: tollReimb, sourceType: "invoice" },
      { category: "Other Income", source: "Adjustment", amount: 0, sourceType: "invoice" }
    ],
    expenseItems: [
      { category: "Fuel", source: "Fuel Module", amount: fuelExp, sourceType: "fuel" },
      { category: "Driver Expenses", source: "Driver Expense", amount: driverExp, sourceType: "driver" },
      { category: "Maintenance", source: "Maintenance", amount: maintExp, sourceType: "maintenance" },
      { category: "Police Expense", source: "Expense Ledger", amount: 0, sourceType: "maintenance" },
      { category: "Challan", source: "Expense Ledger", amount: 0, sourceType: "maintenance" }
    ],
    costSummary: {
      baseTripCharge: baseCharge,
      fuelSurcharge: fuelSurcharge,
      tollReimbursement: tollReimb,
      fuel: fuelExp,
      driverExpense: driverExp,
      maintenance: maintExp
    }
  };
};

const isTripPnLReport = (reportKeyOrTitle = "") => {
  const normalizedValue = normalizeText(reportKeyOrTitle);
  return (
    normalizedValue === "trip-pnl-report" ||
    normalizedValue === normalizeText(REPORT_TYPES[1].title)
  );
};

const VEHICLE_PNL_REPORT_VIEW_SEED = {
  totalFleetRevenue: 1840000,
  totalFleetExpenses: 1220000,
  totalFleetProfit: 620000,
  vehicles: [
    { vehicle: "VHC-00041", type: "Hino 700", trips: 12, revenue: 520000, expenses: 345000, profit: 175000, margin: 33.7 },
    { vehicle: "VHC-00042", type: "MAN TGX", trips: 9, revenue: 480000, expenses: 310000, profit: 170000, margin: 35.4 },
    { vehicle: "VHC-00043", type: "Isuzu NPR", trips: 14, revenue: 560000, expenses: 390000, profit: 170000, margin: 30.4 },
    { vehicle: "VHC-00044", type: "Hino 500", trips: 5, revenue: 280000, expenses: 175000, profit: 105000, margin: 25.4 }
  ],
  costSummary: {
    revenue: 520000,
    fuel: 220000,
    maintenance: 70000,
    parts: 30000,
    driverExpense: 25000
  }
};

const getVehiclePnLReportViewData = (row = {}) => {
  return VEHICLE_PNL_REPORT_VIEW_SEED;
};

const isVehiclePnLReport = (reportKeyOrTitle = "") => {
  const normalizedValue = normalizeText(reportKeyOrTitle);
  return (
    normalizedValue === "vehicle-pnl-report" ||
    normalizedValue === normalizeText(REPORT_TYPES[2].title)
  );
};
const isMonthlyFleetPnLReport = (reportKeyOrTitle = "") => {
  const normalizedValue = normalizeText(reportKeyOrTitle);
  return (
    normalizedValue === "monthly-fleet-pnl" ||
    normalizedValue === normalizeText(REPORT_TYPES[3].title)
  );
};

const MONTHLY_FLEET_PNL_VIEW_SEED = {
  totalRevenue: 2500000,
  totalExpenses: 1750000,
  netProfit: 750000,
  costSummary: {
    fuel: 112500,
    driverExpenses: 7000,
    maintenance: 38500,
    parts: 14700,
  },
  revenueItems: [
    { tripId: "TRP-00041", customer: "ABC Corporations", category: "Base Charge", amount: 80000 },
    { tripId: "TRP-00041", customer: "ABC Corporations", category: "Base Charge", amount: 95000 },
    { tripId: "TRP-00041", customer: "ABC Corporations", category: "Base Charge + Fuel Surcharge", amount: 88000 },
    { tripId: "TRP-00041", customer: "ABC Corporations", category: "Base Charge + Toll", amount: 84500 },
  ],
  fuelItems: [
    { logId: "FL-1042", tripId: "TRP-00041", vehicle: "VHC-00041", amount: 28000 },
    { logId: "FL-1043", tripId: "TRP-00041", vehicle: "VHC-00041", amount: 31000 },
    { logId: "FL-1044", tripId: "TRP-00041", vehicle: "VHC-00041", amount: 25500 },
    { logId: "FL-1045", tripId: "TRP-00041", vehicle: "VHC-00041", amount: 28000 },
  ],
  driverExpenseItems: [
    { expenseId: "EXP-1042", tripId: "TRP-00041", category: "Food", amount: 1600 },
    { expenseId: "EXP-1043", tripId: "TRP-00041", category: "Accommodation", amount: 2000 },
    { expenseId: "EXP-1044", tripId: "TRP-00041", category: "Food", amount: 1800 },
    { expenseId: "EXP-1045", tripId: "TRP-00041", category: "Food", amount: 1600 },
  ],
  maintenanceItems: [
    { woId: "WO-1042", vehicle: "VHC-00041", activity: "Tire Replacement", amount: 18000 },
    { woId: "WO-1043", vehicle: "VHC-00041", activity: "Oil Changed", amount: 8500 },
    { woId: "WO-1044", vehicle: "VHC-00041", activity: "Brakes Service", amount: 12000 },
  ],
  partsItems: [
    { poId: "PO-1042", part: "Brake Pads", vehicle: "VHC-00041", amount: 4000 },
    { poId: "PO-1043", part: "Air Filter", vehicle: "VHC-00041", amount: 1200 },
    { poId: "PO-1044", part: "Fuel Injector", vehicle: "VHC-00041", amount: 9500 },
  ],
};

const getMonthlyFleetPnLReportViewData = (row = {}) => {
  return {
    ...MONTHLY_FLEET_PNL_VIEW_SEED,
    totalRevenue: toFiniteNumber(row?.totalRevenue, MONTHLY_FLEET_PNL_VIEW_SEED.totalRevenue),
    totalExpenses: toFiniteNumber(row?.totalExpense, MONTHLY_FLEET_PNL_VIEW_SEED.totalExpenses),
    netProfit: toFiniteNumber(row?.netProfit, MONTHLY_FLEET_PNL_VIEW_SEED.netProfit),
  };
};

const buildMonthlyFleetPnLReportViewDataFromApi = (report = {}) => {
  const settlements = safeArray(report?.settlement);
  const totalRevenue = toFiniteNumber(report?.total_revenue);
  const totalExpenses = toFiniteNumber(report?.total_expense);
  const netProfit = toFiniteNumber(report?.net_profit);

  const revenueItems = [];
  const fuelItems = [];
  const driverExpenseItems = [];
  const maintenanceItems = [];
  const partsItems = [];

  const costSummary = {
    fuel: 0,
    driverExpenses: 0,
    maintenance: 0,
    parts: 0,
  };

  settlements.forEach((settlement, settlementIndex) => {
    const tripId =
      settlement?.trip_id !== undefined && settlement?.trip_id !== null
        ? String(settlement.trip_id)
        : "-";
    const vehicleId =
      settlement?.vehicle_id !== undefined && settlement?.vehicle_id !== null
        ? String(settlement.vehicle_id)
        : "-";
    const customerId =
      settlement?.customer_id !== undefined && settlement?.customer_id !== null
        ? String(settlement.customer_id)
        : "-";
    const tripRevenue =
      toFiniteNumber(settlement?.trip_net_profit) +
      toFiniteNumber(settlement?.trip_net_expenses);
    const fixCharges = safeArray(settlement?.trip_data?.fix_charges);
    const fuelAmount = toFiniteNumber(settlement?.total_fuel_expense);
    const operationalAmount = toFiniteNumber(settlement?.total_operational_expenses);
    const insuranceAmount = toFiniteNumber(settlement?.total_insurance_expense);
    const shortageAmount = toFiniteNumber(settlement?.total_shortage_expense);

    if (tripRevenue > 0) {
      revenueItems.push({
        tripId,
        customer: `Customer ${customerId}`,
        category: "Trip Revenue",
        amount: tripRevenue,
      });
    }

    if (fuelAmount > 0) {
      fuelItems.push({
        logId: `FUEL-${settlementIndex + 1}`,
        tripId,
        vehicle: vehicleId,
        amount: fuelAmount,
      });
      costSummary.fuel += fuelAmount;
    }

    if (fixCharges.length) {
      fixCharges.forEach((charge, chargeIndex) => {
        const chargeAmount = toFiniteNumber(charge?.rate);
        if (chargeAmount <= 0) return;

        driverExpenseItems.push({
          expenseId: `OPS-${settlementIndex + 1}-${chargeIndex + 1}`,
          tripId,
          category: humanizeExpenseCategory(charge?.type) || "Operational Expense",
          amount: chargeAmount,
        });
      });
    } else if (operationalAmount > 0) {
      driverExpenseItems.push({
        expenseId: `OPS-${settlementIndex + 1}`,
        tripId,
        category: "Operational Expenses",
        amount: operationalAmount,
      });
    }

    costSummary.driverExpenses += operationalAmount;

    if (insuranceAmount > 0) {
      maintenanceItems.push({
        woId: `INS-${settlementIndex + 1}`,
        vehicle: vehicleId,
        activity: "Insurance Expense",
        amount: insuranceAmount,
      });
      costSummary.maintenance += insuranceAmount;
    }

    if (shortageAmount > 0) {
      partsItems.push({
        poId: `SHR-${settlementIndex + 1}`,
        part: "Shortage Expense",
        vehicle: vehicleId,
        amount: shortageAmount,
      });
      costSummary.parts += shortageAmount;
    }
  });

  if (!revenueItems.length && totalRevenue > 0) {
    revenueItems.push({
      tripId: "-",
      customer: "Financial Report",
      category: "Total Revenue",
      amount: totalRevenue,
    });
  }

  if (!fuelItems.length && costSummary.fuel > 0) {
    fuelItems.push({
      logId: "FUEL-TOTAL",
      tripId: "-",
      vehicle: "-",
      amount: costSummary.fuel,
    });
  }

  if (!driverExpenseItems.length && costSummary.driverExpenses > 0) {
    driverExpenseItems.push({
      expenseId: "OPS-TOTAL",
      tripId: "-",
      category: "Operational Expenses",
      amount: costSummary.driverExpenses,
    });
  }

  if (!maintenanceItems.length && costSummary.maintenance > 0) {
    maintenanceItems.push({
      woId: "INS-TOTAL",
      vehicle: "-",
      activity: "Insurance Expense",
      amount: costSummary.maintenance,
    });
  }

  if (!partsItems.length && costSummary.parts > 0) {
    partsItems.push({
      poId: "SHR-TOTAL",
      part: "Shortage Expense",
      vehicle: "-",
      amount: costSummary.parts,
    });
  }

  return {
    totalRevenue,
    totalExpenses,
    netProfit,
    costSummary,
    revenueItems,
    fuelItems,
    driverExpenseItems,
    maintenanceItems,
    partsItems,
  };
};

const MonthlyFleetPnLReportViewModal = ({
  show,
  loading = false,
  reportRow = null,
  reportViewData = null,
  onClose = () => {},
  onDownload = () => {},
}) => {
  const [activeTab, setActiveTab] = useState("revenue");

  useEffect(() => {
    if (show) setActiveTab("revenue");
  }, [show]);

  const renderTable = () => {
    let columns = [];
    let items = [];

    switch (activeTab) {
      case "revenue":
        columns = ["Trip ID", "Customer", "Revenue Category", "Amount (PKR)"];
        items = safeArray(reportViewData?.revenueItems).map((item) => [
          <span className="pnl_link">{item.tripId}</span>,
          item.customer,
          item.category,
          formatAmount(item.amount),
        ]);
        break;
      case "fuel":
        columns = ["Fuel Log ID", "Trip ID", "Vehicle", "Amount (PKR)"];
        items = safeArray(reportViewData?.fuelItems).map((item) => [
          <span className="pnl_link">{item.logId}</span>,
          <span className="pnl_link">{item.tripId}</span>,
          <span className="pnl_link">{item.vehicle}</span>,
          formatAmount(item.amount),
        ]);
        break;
      case "driver-expenses":
        columns = ["Expense ID", "Trip ID", "Category", "Amount (PKR)"];
        items = safeArray(reportViewData?.driverExpenseItems).map((item) => [
          <span className="pnl_link">{item.expenseId}</span>,
          <span className="pnl_link">{item.tripId}</span>,
          item.category,
          formatAmount(item.amount),
        ]);
        break;
      case "maintenance":
        columns = ["Work Order ID", "Vehicle", "Activity", "Amount (PKR)"];
        items = safeArray(reportViewData?.maintenanceItems).map((item) => [
          <span className="pnl_link">{item.woId}</span>,
          <span className="pnl_link">{item.vehicle}</span>,
          item.activity,
          formatAmount(item.amount),
        ]);
        break;
      case "parts":
        columns = ["PO ID", "Part", "Vehicle", "Amount (PKR)"];
        items = safeArray(reportViewData?.partsItems).map((item) => [
          <span className="pnl_link">{item.poId}</span>,
          item.part,
          <span className="pnl_link">{item.vehicle}</span>,
          formatAmount(item.amount),
        ]);
        break;
      default:
        break;
    }

    const hasItems = items.length > 0;

    return (
      <div className="pnl_table_container">
        <table className="financial_report_view_table">
          <thead>
            <tr>
              {columns.map((col, idx) => (
                <th key={idx} style={idx === columns.length - 1 ? { textAlign: "right" } : {}}>
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {hasItems ? (
              items.map((rowItems, rowIdx) => (
                <tr key={rowIdx}>
                  {rowItems.map((cell, cellIdx) => (
                    <td key={cellIdx} style={cellIdx === rowItems.length - 1 ? { textAlign: "right" } : {}}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr className="pnl_table_empty_state">
                <td colSpan={columns.length || 1}>
                  <div className="table_no_records">Record not found</div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <CustomModal
      show={show}
      handleClose={onClose}
      heading="Report View"
      size="xl"
      dialogClassName="financial_report_view_modal financial_report_pnl_modal monthly_fleet_pnl_modal"
    >
      {loading ? (
        <div className="financial_report_view_loading">
          <div className="financial_report_view_spinner" />
          <h5>Compiling Report</h5>
          <p>Please wait while the report is being compiled.</p>
        </div>
      ) : (
        <div className="financial_report_view_content pnl_content">
          <div className="pnl_summary_cards">
            <div className="pnl_summary_card">
              <div className="pnl_card_title">Total Revenue (PKR)</div>
              <div className="pnl_card_amount revenue">{formatAmount(reportViewData?.totalRevenue)}</div>
            </div>
            <div className="pnl_summary_card">
              <div className="pnl_card_title">Total Expenses (PKR)</div>
              <div className="pnl_card_amount expense">{formatAmount(reportViewData?.totalExpenses)}</div>
            </div>
            <div className="pnl_summary_card">
              <div className="pnl_card_title">Net Profit (PKR)</div>
              <div className="pnl_card_amount profit">{formatAmount(reportViewData?.netProfit)}</div>
            </div>
          </div>

          <div className="pnl_tabs_row">
            <div className="pnl_tabs">
              {[
                { key: "revenue", label: "Revenue" },
                { key: "fuel", label: "Fuel" },
                { key: "driver-expenses", label: "Driver Expenses" },
                { key: "maintenance", label: "Maintenance" },
                { key: "parts", label: "Parts" },
              ].map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  className={activeTab === tab.key ? "is_active" : ""}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <div className="pnl_main_layout">
            <div className="pnl_left_panel">{renderTable()}</div>

            <div className="pnl_right_panel">
              <h4>Cost Summary</h4>
              <p>Financial Breakdown</p>

              <div className="pnl_summary_item">
                <span className="pnl_summary_label">Fuel (PKR)</span>
                <span className="pnl_summary_value_box">{formatAmount(reportViewData?.costSummary?.fuel)}</span>
              </div>
              <div className="pnl_summary_item">
                <span className="pnl_summary_label">Driver Expenses (PKR)</span>
                <span className="pnl_summary_value_box">{formatAmount(reportViewData?.costSummary?.driverExpenses)}</span>
              </div>
              <div className="pnl_summary_item">
                <span className="pnl_summary_label">Maintenance (PKR)</span>
                <span className="pnl_summary_value_box">{formatAmount(reportViewData?.costSummary?.maintenance)}</span>
              </div>
              <div className="pnl_summary_item">
                <span className="pnl_summary_label">Parts</span>
                <span className="pnl_summary_value_box">{formatAmount(reportViewData?.costSummary?.parts)}</span>
              </div>
            </div>
          </div>

          <div className="financial_report_view_actions pnl_actions">
            <button type="button" className="cancel_btn" onClick={onClose}>
              Close
            </button>
            <button type="button" className="main_btn financial_report_view_download_btn" onClick={onDownload}>
              <LuDownload />
              Download
            </button>
          </div>
        </div>
      )}
    </CustomModal>
  );
};


const VehiclePnLReportViewModal = ({
  show,
  loading = false,
  reportRow = null,
  reportViewData = null,
  onClose = () => {},
  onDownload = () => {},
}) => {
  const formatCompactPKR = (val) => {
    if (val >= 1000000) return `${(val / 1000000).toFixed(2)}M`;
    if (val >= 1000) return `${(val / 1000).toFixed(0)}K`;
    return val;
  };

  return (
    <CustomModal
      show={show}
      handleClose={onClose}
      heading="Report View"
      size="lg"
      dialogClassName="financial_report_view_modal financial_report_pnl_modal"
    >
      {loading ? (
        <div className="financial_report_view_loading">
          <div className="financial_report_view_spinner" />
          <h5>Compiling Report</h5>
          <p>Please wait while the report is being compiled.</p>
        </div>
      ) : (
        <div className="financial_report_view_content pnl_content">
          <div className="pnl_summary_cards">
            <div className="pnl_summary_card">
              <div className="pnl_card_title">Fleet Revenue (PKR)</div>
              <div className="pnl_card_amount revenue">{formatCompactPKR(reportViewData?.totalFleetRevenue)}</div>
            </div>
            <div className="pnl_summary_card">
              <div className="pnl_card_title">Fleet Expenses (PKR)</div>
              <div className="pnl_card_amount expense">{formatCompactPKR(reportViewData?.totalFleetExpenses)}</div>
            </div>
            <div className="pnl_summary_card">
              <div className="pnl_card_title">Fleet Profit (PKR)</div>
              <div className="pnl_card_amount profit">{formatCompactPKR(reportViewData?.totalFleetProfit)}</div>
            </div>
          </div>

          <div className="pnl_main_layout">
            <div className="pnl_left_panel">
              <div className="pnl_table_container">
                <table className="financial_report_view_table">
                  <thead>
                    <tr>
                      <th>Vehicle</th>
                      <th>Type</th>
                      <th>Trips</th>
                      <th>Revenue</th>
                      <th>Expenses</th>
                      <th>Profit</th>
                      <th>Margin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {safeArray(reportViewData?.vehicles).map((v, idx) => (
                      <tr key={idx}>
                        <td><span className="financial_report_view_link">{v.vehicle}</span></td>
                        <td>{v.type}</td>
                        <td>{v.trips}</td>
                        <td>{formatAmount(v.revenue)}</td>
                        <td>{formatAmount(v.expenses)}</td>
                        <td>{formatAmount(v.profit)}</td>
                        <td>
                          <span className={`pnl_source_badge ${v.margin > 30 ? "invoice" : "maintenance"}`}>
                            {v.margin}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="pnl_right_panel">
              <h4>Cost Summary</h4>
              <p>Financial Breakdown</p>
              
              <div className="pnl_summary_item">
                <span className="pnl_summary_label">Revenue (PKR)</span>
                <span className="pnl_summary_value">{formatAmount(reportViewData?.costSummary?.revenue)}</span>
              </div>
              <div className="pnl_summary_item">
                <span className="pnl_summary_label">Fuel (PKR)</span>
                <span className="pnl_summary_value">{formatAmount(reportViewData?.costSummary?.fuel)}</span>
              </div>
              <div className="pnl_summary_item">
                <span className="pnl_summary_label">Maintenance (PKR)</span>
                <span className="pnl_summary_value">{formatAmount(reportViewData?.costSummary?.maintenance)}</span>
              </div>
              <div className="pnl_summary_item">
                <span className="pnl_summary_label">Parts</span>
                <span className="pnl_summary_value">{formatAmount(reportViewData?.costSummary?.parts)}</span>
              </div>
              <div className="pnl_summary_item">
                <span className="pnl_summary_label">Driver Expense</span>
                <span className="pnl_summary_value">{formatAmount(reportViewData?.costSummary?.driverExpense)}</span>
              </div>
            </div>
          </div>

          <div className="financial_report_view_actions pnl_actions">
            <button type="button" className="cancel_btn" onClick={onClose}>
              Close
            </button>
            <button
              type="button"
              className="main_btn financial_report_view_download_btn"
              onClick={onDownload}
            >
              <LuDownload />
              Download
            </button>
          </div>
        </div>
      )}
    </CustomModal>
  );
};

const TripExpenseReportViewModal = ({
  show,
  loading = false,
  reportRow = null,
  reportViewData = null,
  onClose = () => {},
  onDownload = () => {},
}) => {
  const tripSummaryParts = [
    reportViewData?.tripId || reportRow?.tripId || "",
    reportViewData?.routeLabel || reportRow?.routeLabel || "",
  ].filter((value) => String(value || "").trim() && value !== "-");

  return (
    <CustomModal
      show={show}
      handleClose={onClose}
      heading="Report View"
      size="xl"
      dialogClassName="financial_report_view_modal"
    >
      {loading ? (
        <div className="financial_report_view_loading">
          <div className="financial_report_view_spinner" />
          <h5>Compiling Report</h5>
          <p>Please wait while the report is being compiled.</p>
        </div>
      ) : (
        <div className="financial_report_view_content">
          <div className="financial_report_view_table_card">
            <div className="financial_report_view_table_scroll">
              <table className="financial_report_view_table">
                <thead>
                  <tr>
                    <th>Expense ID</th>
                    <th>Source</th>
                    <th>Trip ID</th>
                    <th>Category</th>
                    <th>Amount (PKR)</th>
                  </tr>
                </thead>
                <tbody>
                  {safeArray(reportViewData?.items).map((item) => (
                    <tr key={`${item.expenseId}-${item.category}`}>
                      <td>
                        <span className="financial_report_view_link">{item.expenseId || "-"}</span>
                      </td>
                      <td>{item.source || "-"}</td>
                      <td>
                        <span className="financial_report_view_link">{item.tripId || "-"}</span>
                      </td>
                      <td>{item.category || "-"}</td>
                      <td>{formatAmount(item.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="financial_report_view_total_card">
            <div>
              <h5>Total Expenses</h5>
              <p>{tripSummaryParts.length ? tripSummaryParts.join(" - ") : "-"}</p>
            </div>

            <div className="financial_report_view_total_amount">
              {formatAmount(reportViewData?.totalAmount || reportRow?.amount)} - PKR
            </div>
          </div>

          <div className="financial_report_view_actions">
            <button type="button" className="cancel_btn" onClick={onClose}>
              Close
            </button>
            <button
              type="button"
              className="main_btn financial_report_view_download_btn"
              onClick={onDownload}
            >
              <LuDownload />
              Download
            </button>
          </div>
        </div>
      )}
    </CustomModal>
  );
};

const TripPnLReportViewModal = ({
  show,
  loading = false,
  reportRow = null,
  reportViewData = null,
  onClose = () => {},
  onDownload = () => {},
}) => {
  const [activeTab, setActiveTab] = useState("revenue");
  const expenseTabLabel = reportViewData?.expenseTabLabel || "Driver Expenses";
  const summaryRows = safeArray(reportViewData?.summaryRows).length
    ? reportViewData.summaryRows
    : [
        { label: "Base Trip Charge (PKR)", value: reportViewData?.costSummary?.baseTripCharge },
        { label: "Fuel Surcharge (PKR)", value: reportViewData?.costSummary?.fuelSurcharge },
        { label: "Toll Reimbursement (PKR)", value: reportViewData?.costSummary?.tollReimbursement },
        { label: "Fuel (PKR)", value: reportViewData?.costSummary?.fuel },
        { label: "Driver Expense (PKR)", value: reportViewData?.costSummary?.driverExpense },
        { label: "Maintenance (PKR)", value: reportViewData?.costSummary?.maintenance },
      ];

  useEffect(() => {
    if (show) setActiveTab("revenue");
  }, [show]);

  const renderTable = () => {
    const isRevenue = activeTab === "revenue";
    const items = isRevenue ? reportViewData?.revenueItems : reportViewData?.expenseItems;
    
    return (
      <div className="pnl_table_container">
        <table className="financial_report_view_table">
          <thead>
            <tr>
              <th>{isRevenue ? "Revenue Category" : "Expense Category"}</th>
              <th>Source</th>
              <th>Amount (PKR)</th>
            </tr>
          </thead>
          <tbody>
            {safeArray(items).map((item, idx) => (
              <tr key={idx}>
                <td>{item.category || "-"}</td>
                <td>
                  <span className={`pnl_source_badge ${item.sourceType || ""}`}>
                    {item.source || "-"}
                  </span>
                </td>
                <td>{formatAmount(item.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <CustomModal
      show={show}
      handleClose={onClose}
      heading="Report View"
      size="lg"
      dialogClassName="financial_report_view_modal financial_report_pnl_modal"
    >
      {loading ? (
        <div className="financial_report_view_loading">
          <div className="financial_report_view_spinner" />
          <h5>Compiling Report</h5>
          <p>Please wait while the report is being compiled.</p>
        </div>
      ) : (
        <div className="financial_report_view_content pnl_content">
          <div className="pnl_summary_cards">
            <div className="pnl_summary_card">
              <div className="pnl_card_title">Total Revenue (PKR)</div>
              <div className="pnl_card_amount revenue">{formatAmount(reportViewData?.totalRevenue)}</div>
            </div>
            <div className="pnl_summary_card">
              <div className="pnl_card_title">Total Expenses (PKR)</div>
              <div className="pnl_card_amount expense">{formatAmount(reportViewData?.totalExpenses)}</div>
            </div>
            <div className="pnl_summary_card">
              <div className="pnl_card_title">Net Profit (PKR)</div>
              <div className="pnl_card_amount profit">{formatAmount(reportViewData?.netProfit)}</div>
            </div>
          </div>

          <div className="pnl_main_layout">
            <div className="pnl_left_panel">
              <div className="pnl_tabs">
                <button
                  type="button"
                  className={activeTab === "revenue" ? "is_active" : ""}
                  onClick={() => setActiveTab("revenue")}
                >
                  Revenue
                </button>
                <button
                  type="button"
                  className={activeTab === "expenses" ? "is_active" : ""}
                  onClick={() => setActiveTab("expenses")}
                >
                  {expenseTabLabel}
                </button>
              </div>

              {renderTable()}

              <div className="financial_report_view_total_card pnl_total_card">
                <div>
                  <h5>{activeTab === "revenue" ? "Total Revenue" : "Total Expenses"}</h5>
                </div>
                <div className="financial_report_view_total_amount">
                  {formatAmount(activeTab === "revenue" ? reportViewData?.totalRevenue : reportViewData?.totalExpenses)} - PKR
                </div>
              </div>
            </div>

            <div className="pnl_right_panel">
              <h4>Cost Summary</h4>
              <p>Financial Breakdown</p>

              {summaryRows.map((item, index) => (
                <div className="pnl_summary_item" key={`${item.label}-${index}`}>
                  <span className="pnl_summary_label">{item.label}</span>
                  <span className="pnl_summary_value">
                    {item.displayValue ?? formatAmount(item.value)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="financial_report_view_actions pnl_actions">
            <button type="button" className="cancel_btn" onClick={onClose}>
              Close
            </button>
            <button
              type="button"
              className="main_btn financial_report_view_download_btn"
              onClick={onDownload}
            >
              <LuDownload />
              Download
            </button>
          </div>
        </div>
      )}
    </CustomModal>
  );
};

const FinancialReports = () => {
  const dispatch = useDispatch();
  const currentUser = useMemo(() => getCurrentUser(), []);
  const { userDropdownData = [] } = useSelector((state) => state.users || {});
  const userDropdownLookup = useMemo(
    () => buildUserDropdownLookup(userDropdownData),
    [userDropdownData]
  );
  const canCreateReports = hasPermission({
    permissionSlug: [ReportsPermission.CreatePnl, ReportsPermission.CreateAr],
    menuSlug: FinanceSubModules.Reports,
    moduleSlug: AppMainModules.finance,
  });

  const [selectedReportType, setSelectedReportType] = useState(REPORT_TYPES[0].key);
  const [formValues, setFormValues] = useState(getReportTypeDefaults(REPORT_TYPES[0].key));
  const [formErrors, setFormErrors] = useState({});
  const [formTouched, setFormTouched] = useState({});
  const [reportRows, setReportRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [generateReportLoading, setGenerateReportLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchFilters, setSearchFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [paginationMeta, setPaginationMeta] = useState({
    totalItems: 0,
    totalPages: 1,
    currentPage: 1,
  });
  const [closedTripOptions, setClosedTripOptions] = useState([]);
  const [closedTripLoading, setClosedTripLoading] = useState(false);
  const [tripReportInfoLoading, setTripReportInfoLoading] = useState(false);
  const [tripReportVehicleOptions, setTripReportVehicleOptions] = useState([]);
  const [tripReportDriverOptions, setTripReportDriverOptions] = useState([]);
  const [tripReportCustomerOptions, setTripReportCustomerOptions] = useState([]);
  const [vehiclePnlOptionsLoading, setVehiclePnlOptionsLoading] = useState(false);
  const [vehiclePnlVehicleDropdownOptions, setVehiclePnlVehicleDropdownOptions] = useState([]);
  const [vehiclePnlCustomerDropdownOptions, setVehiclePnlCustomerDropdownOptions] = useState([]);
  const [tripExpenseReportModal, setTripExpenseReportModal] = useState({
    open: false,
    loading: false,
    reportRow: null,
    reportViewData: null,
  });
  const [tripPnLReportModal, setTripPnLReportModal] = useState({
    open: false,
    loading: false,
    reportRow: null,
    reportViewData: null,
  });
  const [vehiclePnLReportModal, setVehiclePnLReportModal] = useState({
    open: false,
    loading: false,
    reportRow: null,
    reportViewData: null,
  });
  const [monthlyFleetPnLModal, setMonthlyFleetPnLModal] = useState({
    open: false,
    loading: false,
    reportRow: null,
    reportViewData: null,
  });
  const reportCompileTimerRef = useRef(null);
  const tripReportInfoRequestRef = useRef(0);
  const tripExpenseDetailRequestRef = useRef(0);
  const tripPnlDetailRequestRef = useRef(0);
  const vehiclePnlDetailRequestRef = useRef(0);
  const monthlyFleetPnlDetailRequestRef = useRef(0);

  const clearReportCompileTimer = () => {
    if (reportCompileTimerRef.current) {
      window.clearTimeout(reportCompileTimerRef.current);
      reportCompileTimerRef.current = null;
    }
  };

  const clearTripReportInfoOptions = () => {
    setTripReportVehicleOptions([]);
    setTripReportDriverOptions([]);
    setTripReportCustomerOptions([]);
  };

  const fetchFinancialReportHistory = useCallback(
    async ({ page = currentPage, pageLimit = limit, suppressError = false } = {}) => {
      setHistoryLoading(true);

      try {
        const response = await apiService.getFinancialReportsPaginated({
          page,
          limit: pageLimit,
        });
        const pageData = response?.data || response || {};
        const nextRows = safeArray(pageData?.results).map((report) =>
          buildFinancialReportHistoryRow(report, currentUser, userDropdownLookup)
        );
        const nextMeta = pageData?.meta || {};

        setReportRows(nextRows);
        setPaginationMeta({
          totalItems: Math.max(
            nextRows.length,
            toFiniteNumber(nextMeta?.totalItems, toFiniteNumber(pageData?.total, nextRows.length))
          ),
          totalPages: Math.max(1, toFiniteNumber(nextMeta?.totalPages, 1)),
          currentPage: Math.max(1, toFiniteNumber(nextMeta?.currentPage, page)),
        });
      } catch (error) {
        setReportRows([]);
        setPaginationMeta({
          totalItems: 0,
          totalPages: 1,
          currentPage: Math.max(1, page),
        });

        if (!suppressError) {
          errorNotify(error || "Failed to fetch financial reports");
        }
      } finally {
        setHistoryLoading(false);
      }
    },
    [currentPage, currentUser, limit, userDropdownLookup]
  );

  const fetchTripReportInfo = async (tripIds = []) => {
    const normalizedTripIds = toValueArray(tripIds)
      .map((tripId) => Number(tripId))
      .filter((tripId) => Number.isFinite(tripId) && tripId > 0);

    tripReportInfoRequestRef.current += 1;
    const requestId = tripReportInfoRequestRef.current;

    if (!normalizedTripIds.length) {
      clearTripReportInfoOptions();
      setTripReportInfoLoading(false);
      return;
    }

    setTripReportInfoLoading(true);

    try {
      const response = await apiService.getTripReportInfo({
        trip_ids: normalizedTripIds,
      });

      if (tripReportInfoRequestRef.current !== requestId) return;

      const responseData = response?.data || {};
      setTripReportVehicleOptions(
        safeArray(responseData?.vehicles).map(normalizeVehicleDropdownOption)
      );
      setTripReportDriverOptions(
        safeArray(responseData?.drivers).map(normalizeDriverDropdownOption)
      );
      setTripReportCustomerOptions(
        safeArray(responseData?.customers).map(normalizeCustomerDropdownOption)
      );
    } catch (error) {
      if (tripReportInfoRequestRef.current !== requestId) return;

      clearTripReportInfoOptions();
      errorNotify(error || "Failed to fetch trip report info");
    } finally {
      if (tripReportInfoRequestRef.current === requestId) {
        setTripReportInfoLoading(false);
      }
    }
  };

  const closeTripExpenseReportModal = () => {
    clearReportCompileTimer();
    tripExpenseDetailRequestRef.current += 1;
    setTripExpenseReportModal({
      open: false,
      loading: false,
      reportRow: null,
      reportViewData: null,
    });
  };

  const closeTripPnLReportModal = () => {
    clearReportCompileTimer();
    tripPnlDetailRequestRef.current += 1;
    setTripPnLReportModal({
      open: false,
      loading: false,
      reportRow: null,
      reportViewData: null,
    });
  };

  const closeVehiclePnLReportModal = () => {
    clearReportCompileTimer();
    vehiclePnlDetailRequestRef.current += 1;
    setVehiclePnLReportModal({
      open: false,
      loading: false,
      reportRow: null,
      reportViewData: null,
    });
  };

  const closeMonthlyFleetPnLModal = () => {
    clearReportCompileTimer();
    monthlyFleetPnlDetailRequestRef.current += 1;
    setMonthlyFleetPnLModal({
      open: false,
      loading: false,
      reportRow: null,
      reportViewData: null,
    });
  };

  useEffect(() => {
    if (userDropdownData.length) return;
    dispatch(getUserDropdown());
  }, [dispatch, userDropdownData.length]);

  useEffect(() => {
    setCurrentPage(1);
  }, [limit, searchFilters, searchText]);

  useEffect(() => {
    let isMounted = true;

    const fetchClosedTripOptions = async () => {
      setClosedTripLoading(true);

      try {
        const response = await apiService.getClosedTripsDropdown();
        const nextOptions = Array.isArray(response?.data)
          ? response.data.map(normalizeTripDropdownOption)
          : [];

        if (!isMounted) return;
        setClosedTripOptions(nextOptions);
      } catch (error) {
        if (!isMounted) return;
        setClosedTripOptions([]);
        errorNotify(error || "Failed to fetch closed trips dropdown");
      } finally {
        if (isMounted) {
          setClosedTripLoading(false);
        }
      }
    };

    fetchClosedTripOptions();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (
      selectedReportType !== "vehicle-pnl-report" &&
      selectedReportType !== "monthly-fleet-pnl"
    ) return;
    if (vehiclePnlVehicleDropdownOptions.length && vehiclePnlCustomerDropdownOptions.length) return;

    let isMounted = true;

    const fetchVehiclePnlDropdowns = async () => {
      setVehiclePnlOptionsLoading(true);

      try {
        const [vehiclesResponse, customersResponse] = await Promise.all([
          apiService.getVehiclesDropdown(),
          apiService.getCustomerDropdown(),
        ]);

        if (!isMounted) return;

        const resolvedVehicles = safeArray(vehiclesResponse?.data || vehiclesResponse).map(
          normalizeVehicleDropdownOption
        );
        const resolvedCustomers = safeArray(customersResponse?.data || customersResponse).map(
          normalizeCustomerDropdownOption
        );

        setVehiclePnlVehicleDropdownOptions(resolvedVehicles);
        setVehiclePnlCustomerDropdownOptions(resolvedCustomers);
      } catch (error) {
        if (!isMounted) return;

        setVehiclePnlVehicleDropdownOptions([]);
        setVehiclePnlCustomerDropdownOptions([]);
        errorNotify(error || "Failed to fetch vehicle PnL dropdowns");
      } finally {
        if (isMounted) {
          setVehiclePnlOptionsLoading(false);
        }
      }
    };

    fetchVehiclePnlDropdowns();

    return () => {
      isMounted = false;
    };
  }, [
    selectedReportType,
    vehiclePnlCustomerDropdownOptions.length,
    vehiclePnlVehicleDropdownOptions.length,
  ]);

  useEffect(() => () => clearReportCompileTimer(), []);

  useEffect(() => {
    fetchFinancialReportHistory({
      page: currentPage,
      pageLimit: limit,
    });
  }, [currentPage, fetchFinancialReportHistory, limit]);

  const combinedTripOptions = useMemo(
    () => mergeTripOptions(REPORT_TRIP_OPTIONS, closedTripOptions),
    [closedTripOptions]
  );

  const tripPnLTripOptions = useMemo(
    () => [ALL_TRIPS_OPTION, ...closedTripOptions],
    [closedTripOptions]
  );

  const tripPnlVehicleOptions = useMemo(
    () =>
      buildExclusiveAllOptions(
        tripReportVehicleOptions,
        ALL_VEHICLES_OPTION,
        formValues.vehicleId
      ),
    [formValues.vehicleId, tripReportVehicleOptions]
  );

  const tripPnlCustomerOptions = useMemo(
    () =>
      buildExclusiveAllOptions(
        tripReportCustomerOptions,
        ALL_CUSTOMERS_OPTION,
        formValues.customerId
      ),
    [formValues.customerId, tripReportCustomerOptions]
  );

  const tripPnlSelectableTripOptions = useMemo(
    () => buildExclusiveAllOptions(tripPnLTripOptions, ALL_TRIPS_OPTION, formValues.tripId),
    [formValues.tripId, tripPnLTripOptions]
  );

  const vehiclePnlSelectableVehicleOptions = useMemo(
    () =>
      buildExclusiveAllOptions(
        vehiclePnlVehicleDropdownOptions,
        ALL_VEHICLES_OPTION,
        formValues.vehicleId
      ),
    [formValues.vehicleId, vehiclePnlVehicleDropdownOptions]
  );

  const vehiclePnlSelectableCustomerOptions = useMemo(
    () =>
      buildExclusiveAllOptions(
        vehiclePnlCustomerDropdownOptions,
        ALL_CUSTOMERS_OPTION,
        formValues.customerId
      ),
    [formValues.customerId, vehiclePnlCustomerDropdownOptions]
  );

  const tripLookup = useMemo(
    () =>
      combinedTripOptions.reduce((lookup, trip) => {
        lookup[String(trip.value)] = trip;
        return lookup;
      }, {}),
    [combinedTripOptions]
  );

  const selectedReportDefinition = useMemo(
    () => REPORT_TYPES.find((reportType) => reportType.key === selectedReportType) || REPORT_TYPES[0],
    [selectedReportType]
  );

  const activeFormFields = useMemo(() => {
    const baseFields =
      REPORT_FORM_FIELDS[selectedReportType] || REPORT_FORM_FIELDS[REPORT_TYPES[0].key];

    if (
      selectedReportType !== "trip-expense-report" &&
      selectedReportType !== "trip-pnl-report" &&
      selectedReportType !== "vehicle-pnl-report" &&
      selectedReportType !== "monthly-fleet-pnl"
    ) {
      return baseFields;
    }

    return baseFields.map((field) => {
      if (selectedReportType === "trip-pnl-report") {
        const isAllTripsSelected = safeArray(formValues.tripId).includes(ALL_TRIPS_OPTION.value);
        const hasSelectedTripId = safeArray(formValues.tripId).length > 0;

        if (field.name === "tripId") {
          return {
            ...field,
            options: tripPnlSelectableTripOptions,
            loading: closedTripLoading,
            placeholder: closedTripLoading ? "Loading Trip IDs..." : field.placeholder,
          };
        }

        if (field.name === "vehicleId") {
          return {
            ...field,
            options: tripPnlVehicleOptions,
            loading: tripReportInfoLoading,
            placeholder: !hasSelectedTripId
              ? "Select Trip ID first"
              : tripReportInfoLoading
                ? "Loading Vehicles..."
                : field.placeholder,
            isDisabled: !hasSelectedTripId || tripReportInfoLoading,
          };
        }

        if (field.name === "customerId") {
          return {
            ...field,
            options: tripPnlCustomerOptions,
            loading: tripReportInfoLoading,
            placeholder: !hasSelectedTripId
              ? "Select Trip ID first"
              : tripReportInfoLoading
                ? "Loading Customers..."
                : field.placeholder,
            isDisabled: !hasSelectedTripId || tripReportInfoLoading,
          };
        }
      }

      if (selectedReportType === "vehicle-pnl-report") {
        if (field.name === "vehicleId") {
          return {
            ...field,
            options: vehiclePnlSelectableVehicleOptions,
            loading: vehiclePnlOptionsLoading,
            placeholder: vehiclePnlOptionsLoading ? "Loading Vehicles..." : field.placeholder,
            isDisabled: vehiclePnlOptionsLoading,
          };
        }

        if (field.name === "customerId") {
          return {
            ...field,
            options: vehiclePnlSelectableCustomerOptions,
            loading: vehiclePnlOptionsLoading,
            placeholder: vehiclePnlOptionsLoading ? "Loading Customers..." : field.placeholder,
            isDisabled: vehiclePnlOptionsLoading,
          };
        }
      }

      if (selectedReportType === "monthly-fleet-pnl") {
        if (field.name === "vehicleId") {
          return {
            ...field,
            options: vehiclePnlSelectableVehicleOptions,
            loading: vehiclePnlOptionsLoading,
            placeholder: vehiclePnlOptionsLoading ? "Loading Vehicles..." : field.placeholder,
            isDisabled: vehiclePnlOptionsLoading,
          };
        }
      }

      if (field.name === "tripId") {
        return {
          ...field,
          options: closedTripOptions,
          loading: closedTripLoading,
          placeholder: closedTripLoading ? "Loading Trip IDs..." : field.placeholder,
        };
      }

      if (field.name === "exportFormat") {
        return {
          ...field,
          isDisabled: true,
        };
      }

      if (field.name === "vehicleId") {
        const hasSelectedTripIds = toValueArray(formValues.tripId).length > 0;
        return {
          ...field,
          options: tripReportVehicleOptions,
          loading: tripReportInfoLoading,
          placeholder: hasSelectedTripIds
            ? tripReportInfoLoading
              ? "Loading Vehicles..."
              : field.placeholder
            : "Select Trip ID first",
          isDisabled: !hasSelectedTripIds || tripReportInfoLoading,
        };
      }

      if (field.name === "driverId") {
        const hasSelectedTripIds = toValueArray(formValues.tripId).length > 0;
        return {
          ...field,
          options: tripReportDriverOptions,
          loading: tripReportInfoLoading,
          placeholder: hasSelectedTripIds
            ? tripReportInfoLoading
              ? "Loading Drivers..."
              : field.placeholder
            : "Select Trip ID first",
          isDisabled: !hasSelectedTripIds || tripReportInfoLoading,
        };
      }

      return field;
    });
  }, [
    closedTripLoading,
    closedTripOptions,
    formValues.tripId,
    selectedReportType,
    tripReportDriverOptions,
    tripReportInfoLoading,
    tripReportCustomerOptions,
    tripReportVehicleOptions,
    vehiclePnlOptionsLoading,
    vehiclePnlSelectableCustomerOptions,
    vehiclePnlSelectableVehicleOptions,
    tripPnlCustomerOptions,
    tripPnlSelectableTripOptions,
    tripPnLTripOptions,
    tripPnlVehicleOptions,
  ]);

  const selectedReportDefaults = useMemo(
    () => getReportTypeDefaults(selectedReportType),
    [selectedReportType]
  );

  const hasClientFilters = Boolean(normalizeText(searchText)) ||
    Object.keys(searchFilters || {}).length > 0;

  const filteredRows = useMemo(() => {
    const filterAccessors = {
      reportIdFilter: (row) => row.reportId,
      reportTypeFilter: (row) => row.reportType,
      generatedOnFilter: (row) => row.generatedOnValue,
      generatedByFilter: (row) => row.generatedBy,
      dateRangeFilter: (row) => row.dateRangeLabel,
      exportFormatFilter: (row) => row.exportFormat,
      revenueFilter: (row) => row.totalRevenue,
      expenseFilter: (row) => row.totalExpense,
      netProfitFilter: (row) => row.netProfit,
    };

    return reportRows.filter((row) => {
      if (!matchesSearchTerm(row, searchText)) return false;

      return Object.entries(searchFilters || {}).every(([filterName, filterMeta]) => {
        const accessor = filterAccessors[filterName];
        if (!accessor) return true;
        return matchesFilter(accessor(row), filterMeta);
      });
    });
  }, [reportRows, searchFilters, searchText]);

  const totalItems = hasClientFilters
    ? filteredRows.length
    : Math.max(filteredRows.length, toFiniteNumber(paginationMeta.totalItems, filteredRows.length));
  const totalPages = hasClientFilters
    ? Math.max(1, Math.ceil(Math.max(filteredRows.length, 1) / limit))
    : Math.max(1, toFiniteNumber(paginationMeta.totalPages, 1));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedRows = useMemo(() => {
    if (!hasClientFilters) {
      return filteredRows;
    }

    const startIndex = (currentPage - 1) * limit;
    return filteredRows.slice(startIndex, startIndex + limit);
  }, [currentPage, filteredRows, hasClientFilters, limit]);

  const isFormDirty = useMemo(
    () =>
      Object.keys(INITIAL_FORM_VALUES).some(
        (key) => String(formValues[key] || "") !== String(selectedReportDefaults[key] || "")
      ),
    [formValues, selectedReportDefaults]
  );

  const isFormReady = useMemo(() => {
    const requiredFields = activeFormFields
      .filter((field) => field.required !== false)
      .map((field) => formValues[field.name]);

    if (requiredFields.some((value) => !String(value || "").trim())) {
      return false;
    }

    if (
      formValues.startDate &&
      formValues.endDate &&
      new Date(formValues.endDate).getTime() < new Date(formValues.startDate).getTime()
    ) {
      return false;
    }

    return true;
  }, [activeFormFields, formValues]);

  const setFieldValue = (fieldName, value) => {
    setFormValues((previous) => ({
      ...previous,
      [fieldName]: value,
    }));

    setFormErrors((previous) => {
      if (!previous?.[fieldName]) return previous;
      const nextErrors = { ...previous };
      delete nextErrors[fieldName];
      return nextErrors;
    });
  };

  const setFieldTouched = (fieldName) => {
    setFormTouched((previous) => ({
      ...previous,
      [fieldName]: true,
    }));
  };

  const handleReset = () => {
    setFormValues(selectedReportDefaults);
    setFormErrors({});
    setFormTouched({});

    if (
      selectedReportType === "trip-expense-report" ||
      selectedReportType === "trip-pnl-report"
    ) {
      tripReportInfoRequestRef.current += 1;
      setTripReportInfoLoading(false);
      clearTripReportInfoOptions();
    }
  };

  const validateForm = () => {
    const nextErrors = {};

    activeFormFields.forEach((field) => {
      if (field.required === false) return;
      if (!String(formValues[field.name] || "").trim()) {
        nextErrors[field.name] = `${field.label} is required`;
      }
    });

    if (
      formValues.startDate &&
      formValues.endDate &&
      new Date(formValues.endDate).getTime() < new Date(formValues.startDate).getTime()
    ) {
      nextErrors.endDate = "End date must be on or after start date";
    }

    return nextErrors;
  };

  const handleReportTypeChange = (reportTypeKey) => {
    setSelectedReportType(reportTypeKey);
    setFormValues(getReportTypeDefaults(reportTypeKey));
    setFormErrors({});
    setFormTouched({});
    tripReportInfoRequestRef.current += 1;
    setTripReportInfoLoading(false);

    if (reportTypeKey === "trip-expense-report") {
      clearTripReportInfoOptions();
      return;
    }

    clearTripReportInfoOptions();
  };

  const handleSelectFieldChange = (field, option) => {
    const nextValue = field.isMulti
      ? safeArray(option).map((selectedOption) => selectedOption?.value).filter(Boolean)
      : option?.value || "";

    if (field.name === "tripId" && selectedReportType === "trip-expense-report") {
      setFieldValue("tripId", nextValue);
      setFieldValue("vehicleId", []);
      setFieldValue("driverId", []);
      fetchTripReportInfo(nextValue);
      return;
    }

    if (field.name === "tripId" && selectedReportType === "trip-pnl-report") {
      const normalizedTripValues = normalizeExclusiveMultiValue(
        nextValue,
        ALL_TRIPS_OPTION.value
      );

      setFieldValue("tripId", normalizedTripValues);
      setFieldValue("vehicleId", []);
      setFieldValue("customerId", []);

      if (normalizedTripValues.includes(ALL_TRIPS_OPTION.value)) {
        fetchTripReportInfo(closedTripOptions.map((trip) => trip?.value));
        return;
      }

      fetchTripReportInfo(normalizedTripValues);
      return;
    }

    if (field.name === "vehicleId" && selectedReportType === "trip-pnl-report") {
      setFieldValue(
        "vehicleId",
        normalizeExclusiveMultiValue(nextValue, ALL_VEHICLES_OPTION.value)
      );
      return;
    }

    if (field.name === "customerId" && selectedReportType === "trip-pnl-report") {
      setFieldValue(
        "customerId",
        normalizeExclusiveMultiValue(nextValue, ALL_CUSTOMERS_OPTION.value)
      );
      return;
    }

    if (field.name === "vehicleId" && selectedReportType === "vehicle-pnl-report") {
      setFieldValue(
        "vehicleId",
        normalizeExclusiveMultiValue(nextValue, ALL_VEHICLES_OPTION.value)
      );
      return;
    }

    if (field.name === "customerId" && selectedReportType === "vehicle-pnl-report") {
      setFieldValue(
        "customerId",
        normalizeExclusiveMultiValue(nextValue, ALL_CUSTOMERS_OPTION.value)
      );
      return;
    }

    if (field.name === "vehicleId" && selectedReportType === "monthly-fleet-pnl") {
      setFieldValue(
        "vehicleId",
        normalizeExclusiveMultiValue(nextValue, ALL_VEHICLES_OPTION.value)
      );
      return;
    }

    setFieldValue(field.name, nextValue);
  };

  const openTripExpenseReportModal = async (row = {}, showLoading = false) => {
    clearReportCompileTimer();

    const shouldFetchApiDetail = row?.hasDetailedPreview === false && row?.id;

    if (shouldFetchApiDetail) {
      tripExpenseDetailRequestRef.current += 1;
      const requestId = tripExpenseDetailRequestRef.current;

      setTripExpenseReportModal({
        open: true,
        loading: true,
        reportRow: row,
        reportViewData: null,
      });

      try {
        const response = await apiService.getTripExpenseReportDetail(row.id);
        const responseData = response?.data || {};
        const reportViewData = buildTripExpenseReportViewDataFromApi(responseData);
        const nextReportRow = {
          ...row,
          tripId: reportViewData.tripId || row.tripId || "-",
          routeLabel: reportViewData.routeLabel || row.routeLabel || "-",
          expenseItems: reportViewData.items,
          amount: reportViewData.totalAmount || row.amount,
          totalExpense: reportViewData.totalAmount || row.totalExpense,
          hasDetailedPreview: true,
        };

        if (tripExpenseDetailRequestRef.current !== requestId) return;

        setTripExpenseReportModal({
          open: true,
          loading: false,
          reportRow: nextReportRow,
          reportViewData,
        });
      } catch (error) {
        if (tripExpenseDetailRequestRef.current !== requestId) return;

        setTripExpenseReportModal({
          open: false,
          loading: false,
          reportRow: null,
          reportViewData: null,
        });
        errorNotify(error || "Failed to fetch trip expense report detail");
      }

      return;
    }

    const reportViewData = getTripExpenseReportViewData(row);
    const nextReportRow = {
      ...row,
      amount: reportViewData.totalAmount || row.amount,
      routeLabel: reportViewData.routeLabel || row.routeLabel || "-",
      expenseItems: reportViewData.items,
      tripId: reportViewData.tripId || row.tripId || "-",
    };

    setTripExpenseReportModal({
      open: true,
      loading: showLoading,
      reportRow: nextReportRow,
      reportViewData,
    });

    if (showLoading) {
      reportCompileTimerRef.current = window.setTimeout(() => {
        setTripExpenseReportModal((previous) =>
          previous.open
            ? {
                ...previous,
                loading: false,
              }
            : previous
        );
        reportCompileTimerRef.current = null;
      }, 1400);
    }
  };

  const openTripPnLReportModal = async (row = {}, showLoading = false) => {
    clearReportCompileTimer();

    const shouldFetchApiDetail = row?.hasDetailedPreview === false && row?.id;

    if (shouldFetchApiDetail) {
      tripPnlDetailRequestRef.current += 1;
      const requestId = tripPnlDetailRequestRef.current;

      setTripPnLReportModal({
        open: true,
        loading: true,
        reportRow: row,
        reportViewData: null,
      });

      try {
        const response = await apiService.getTripPnlReportDetail(row.id);
        const responseData = response?.data || {};
        const reportViewData = buildTripPnlReportViewDataFromApi(responseData);
        const nextReportRow = {
          ...row,
          tripId: reportViewData.tripId || row.tripId || "-",
          routeLabel: reportViewData.routeLabel || row.routeLabel || "-",
          totalRevenue: reportViewData.totalRevenue,
          totalExpense: reportViewData.totalExpenses,
          netProfit: reportViewData.netProfit,
          amount: reportViewData.netProfit,
          profitMargin: toFiniteNumber(responseData?.profit_margin, row?.profitMargin),
          apiTripPnlViewData: reportViewData,
          hasDetailedPreview: true,
        };

        if (tripPnlDetailRequestRef.current !== requestId) return;

        setTripPnLReportModal({
          open: true,
          loading: false,
          reportRow: nextReportRow,
          reportViewData,
        });
      } catch (error) {
        if (tripPnlDetailRequestRef.current !== requestId) return;

        setTripPnLReportModal({
          open: false,
          loading: false,
          reportRow: null,
          reportViewData: null,
        });
        errorNotify(error || "Failed to fetch trip PnL report detail");
      }

      return;
    }

    const reportViewData = getTripPnLReportViewData(row);
    const nextReportRow = {
      ...row,
      apiTripPnlViewData: reportViewData,
      tripId: reportViewData.tripId || row.tripId || "-",
    };

    setTripPnLReportModal({
      open: true,
      loading: showLoading,
      reportRow: nextReportRow,
      reportViewData,
    });

    if (showLoading) {
      reportCompileTimerRef.current = window.setTimeout(() => {
        setTripPnLReportModal((previous) =>
          previous.open
            ? {
                ...previous,
                loading: false,
              }
            : previous
        );
        reportCompileTimerRef.current = null;
      }, 1400);
    }
  };

  const openVehiclePnLReportModal = async (row = {}, showLoading = false) => {
    clearReportCompileTimer();

    const shouldFetchApiDetail = row?.hasDetailedPreview === false && row?.id;

    if (shouldFetchApiDetail) {
      vehiclePnlDetailRequestRef.current += 1;
      const requestId = vehiclePnlDetailRequestRef.current;

      setVehiclePnLReportModal({
        open: true,
        loading: true,
        reportRow: row,
        reportViewData: null,
      });

      try {
        const response = await apiService.getVehiclePnlReportDetail(row.id);
        const responseData = response?.data || {};
        const reportViewData = buildVehiclePnLReportViewDataFromApi(responseData);
        const nextReportRow = {
          ...row,
          totalRevenue: toFiniteNumber(responseData?.total_revenue, row?.totalRevenue),
          totalExpense: toFiniteNumber(responseData?.total_expense, row?.totalExpense),
          netProfit: toFiniteNumber(responseData?.net_profit, row?.netProfit),
          amount: toFiniteNumber(responseData?.net_profit, row?.amount),
          profitMargin: toFiniteNumber(responseData?.profit_margin, row?.profitMargin),
          apiVehiclePnlViewData: reportViewData,
          hasDetailedPreview: true,
        };

        if (vehiclePnlDetailRequestRef.current !== requestId) return;

        setVehiclePnLReportModal({
          open: true,
          loading: false,
          reportRow: nextReportRow,
          reportViewData,
        });
      } catch (error) {
        if (vehiclePnlDetailRequestRef.current !== requestId) return;

        setVehiclePnLReportModal({
          open: false,
          loading: false,
          reportRow: null,
          reportViewData: null,
        });
        errorNotify(error || "Failed to fetch vehicle PnL report detail");
      }

      return;
    }

    const reportViewData = getVehiclePnLReportViewData(row);

    setVehiclePnLReportModal({
      open: true,
      loading: showLoading,
      reportRow: row,
      reportViewData,
    });

    if (showLoading) {
      reportCompileTimerRef.current = window.setTimeout(() => {
        setVehiclePnLReportModal((previous) =>
          previous.open
            ? {
                ...previous,
                loading: false,
              }
            : previous
        );
        reportCompileTimerRef.current = null;
      }, 1400);
    }
  };
  const openMonthlyFleetPnLModal = async (row = {}, showLoading = false) => {
    clearReportCompileTimer();

    const shouldFetchApiDetail = row?.hasDetailedPreview === false && row?.id;

    if (shouldFetchApiDetail) {
      monthlyFleetPnlDetailRequestRef.current += 1;
      const requestId = monthlyFleetPnlDetailRequestRef.current;

      setMonthlyFleetPnLModal({
        open: true,
        loading: true,
        reportRow: row,
        reportViewData: null,
      });

      try {
        const response = await apiService.getMonthlyFleetPnlReportDetail(row.id);
        const responseData = response?.data || {};
        const reportViewData = buildMonthlyFleetPnLReportViewDataFromApi(responseData);
        const nextReportRow = {
          ...row,
          totalRevenue: toFiniteNumber(responseData?.total_revenue, row?.totalRevenue),
          totalExpense: toFiniteNumber(responseData?.total_expense, row?.totalExpense),
          netProfit: toFiniteNumber(responseData?.net_profit, row?.netProfit),
          amount: toFiniteNumber(responseData?.net_profit, row?.amount),
          profitMargin: toFiniteNumber(responseData?.profit_margin, row?.profitMargin),
          monthName: responseData?.month_name || row?.monthName || "-",
          dateRangeLabel: responseData?.month_name || row?.dateRangeLabel || "-",
          apiMonthlyFleetPnlViewData: reportViewData,
          hasDetailedPreview: true,
        };

        if (monthlyFleetPnlDetailRequestRef.current !== requestId) return;

        setMonthlyFleetPnLModal({
          open: true,
          loading: false,
          reportRow: nextReportRow,
          reportViewData,
        });
      } catch (error) {
        if (monthlyFleetPnlDetailRequestRef.current !== requestId) return;

        setMonthlyFleetPnLModal({
          open: false,
          loading: false,
          reportRow: null,
          reportViewData: null,
        });
        errorNotify(error || "Failed to fetch monthly fleet PnL report detail");
      }

      return;
    }

    const reportViewData = getMonthlyFleetPnLReportViewData(row);

    setMonthlyFleetPnLModal({
      open: true,
      loading: showLoading,
      reportRow: row,
      reportViewData,
    });

    if (showLoading) {
      reportCompileTimerRef.current = window.setTimeout(() => {
        setMonthlyFleetPnLModal((previous) =>
          previous.open
            ? {
                ...previous,
                loading: false,
              }
            : previous
        );
        reportCompileTimerRef.current = null;
      }, 1400);
    }
  };

  const downloadReport = (row = {}) => {
    const exportFormat = String(row.exportFormat || "pdf").toLowerCase();
    const supportsDetailedPreview = row?.hasDetailedPreview !== false;
    const isPnL = isTripPnLReport(row.reportTypeKey || row.reportType);
    const pnlData = supportsDetailedPreview && isPnL ? getTripPnLReportViewData(row) : null;
    const tripExpenseItems = supportsDetailedPreview
      ? isTripExpenseReport(row.reportTypeKey || row.reportType)
        ? safeArray(row.expenseItems).length
          ? row.expenseItems
          : getTripExpenseReportViewData(row).items
        : isPnL
          ? [...safeArray(pnlData?.revenueItems), ...safeArray(pnlData?.expenseItems)]
          : []
      : [];
    const baseIdentifier =
      row.reportId ||
      (row.tripId && row.tripId !== "-" ? row.tripId : row.reportType || "financial-report");
    const baseFileName = `${String(baseIdentifier)
      .replace(/[^a-z0-9]+/gi, "-")
      .replace(/^-+|-+$/g, "")
      .toLowerCase()}-${String(row.generatedOnValue || row.generatedOnDisplay || "").replace(/[^0-9]/g, "") || Date.now()}`;

    if (exportFormat === "excel" || exportFormat === "csv") {
      const worksheet = XLSX.utils.json_to_sheet(
        tripExpenseItems.length
          ? tripExpenseItems.map((item) => ({
              "Expense ID": item.expenseId,
              Source: item.source,
              "Trip ID": item.tripId,
              Category: item.category,
              "Amount (PKR)": item.amount,
            }))
          : [
              {
                "Report ID": row.reportId,
                "Report Type": row.reportType,
                "Generated On": row.generatedOnDisplay || row.generatedOnValue,
                "Generated By": row.generatedBy,
                "Date Range": row.dateRangeLabel || `${row.startDate || "-"} to ${row.endDate || "-"}`,
                "Export Format": String(row.exportFormat || "-").toUpperCase(),
                "Total Revenue (PKR)": row.totalRevenue,
                "Total Expense (PKR)": row.totalExpense,
                "Net Profit (PKR)": row.netProfit,
                "Profit Margin (%)": toFiniteNumber(row.profitMargin).toFixed(2),
                "Amount (PKR)": row.amount,
              },
            ]
      );

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Financial Report");
      XLSX.writeFile(workbook, `${baseFileName}.${exportFormat === "csv" ? "csv" : "xlsx"}`, {
        bookType: exportFormat === "csv" ? "csv" : "xlsx",
      });
      return;
    }

    const pdf = new jsPDF();
    pdf.setFontSize(18);
    pdf.text(row.reportType || "Financial Report", 14, 22);
    pdf.setFontSize(11);

    const reportLines = [
      `Report ID: ${row.reportId || "-"}`,
      `Generated On: ${row.generatedOnDisplay || row.generatedOnValue || "-"}`,
      `Generated By: ${row.generatedBy || "-"}`,
      `Date Range: ${row.dateRangeLabel || `${row.startDate || "-"} to ${row.endDate || "-"}`}`,
      `Export Format: ${String(row.exportFormat || "-").toUpperCase()}`,
      `Total Revenue (PKR): ${formatAmount(row.totalRevenue)}`,
      `Total Expense (PKR): ${formatAmount(row.totalExpense)}`,
      `Net Profit (PKR): ${formatAmount(row.netProfit)}`,
      `Profit Margin: ${toFiniteNumber(row.profitMargin).toFixed(2)}%`,
      `Amount (PKR): ${formatAmount(row.amount)}`,
    ];

    reportLines.forEach((line, index) => {
      pdf.text(line, 14, 36 + index * 10);
    });

    if (tripExpenseItems.length) {
      let nextLineY = 36 + reportLines.length * 10 + 10;
      pdf.setFontSize(12);
      pdf.text("Expense Breakdown", 14, nextLineY);
      nextLineY += 10;
      pdf.setFontSize(10);

      tripExpenseItems.forEach((item) => {
        const itemLine = `${item.expenseId} | ${item.source} | ${item.category} | PKR ${formatAmount(
          item.amount
        )}`;
        pdf.text(itemLine, 14, nextLineY);
        nextLineY += 8;
      });
    }

    pdf.save(`${baseFileName}.pdf`);
  };

  const previewReport = (row = {}) => {
    if (isTripExpenseReport(row.reportTypeKey || row.reportType)) {
      openTripExpenseReportModal(row, false);
      return;
    }

    if (isTripPnLReport(row.reportTypeKey || row.reportType)) {
      openTripPnLReportModal(row, false);
      return;
    }

    if (isVehiclePnLReport(row.reportTypeKey || row.reportType)) {
      openVehiclePnLReportModal(row, false);
      return;
    }

    if (isMonthlyFleetPnLReport(row.reportTypeKey || row.reportType)) {
      openMonthlyFleetPnLModal(row, false);
      return;
    }

    const supportsDetailedPreview = row?.hasDetailedPreview !== false;

    const previewWindow = window.open("", "_blank", "noopener,noreferrer");
    if (!previewWindow) {
      errorNotify("Please allow popups to preview the financial report");
      return;
    }

    previewWindow.document.write(buildReportPreviewHtml(row));
    previewWindow.document.close();
  };

  const handleGenerateReport = async () => {
    if (!canCreateReports || generateReportLoading) return;

    const nextErrors = validateForm();
    const touchedFields = activeFormFields.reduce(
      (fields, key) => ({
        ...fields,
        [key.name]: true,
      }),
      {}
    );

    setFormTouched(touchedFields);
    setFormErrors(nextErrors);

    if (Object.keys(nextErrors).length) {
      errorNotify("Please complete all required financial report fields");
      return;
    }

    setGenerateReportLoading(true);

    try {
      let tripExpenseResponse = null;
      let tripPnlResponse = null;
      let vehiclePnlResponse = null;
      let monthlyFleetPnlResponse = null;

      if (selectedReportType === "trip-expense-report") {
        try {
          const payload = {
            trip_ids: toValueArray(formValues.tripId).map(id => {
              const num = Number(id);
              return isNaN(num) ? id : num;
            }),
            vehicle_ids: toValueArray(formValues.vehicleId).map(id => {
              const num = Number(id);
              return isNaN(num) ? id : num;
            }),
            driver_ids: toValueArray(formValues.driverId).map(id => {
              const num = Number(id);
              return isNaN(num) ? id : num;
            }),
            export_format: formValues.exportFormat || "pdf",
            start_date: formValues.startDate || "",
            end_date: formValues.endDate || ""
          };

          tripExpenseResponse = await apiService.generateTripExpenseReport(payload);
        } catch (error) {
          errorNotify(error);
          return;
        }
      }

      if (selectedReportType === "trip-pnl-report") {
        try {
          const payload = {
            trip_ids: expandAllSelectionToPayloadIds({
              values: formValues.tripId,
              options: closedTripOptions,
              allOptionValue: ALL_TRIPS_OPTION.value,
            }),
            vehicle_ids: expandAllSelectionToPayloadIds({
              values: formValues.vehicleId,
              options: tripReportVehicleOptions,
              allOptionValue: ALL_VEHICLES_OPTION.value,
            }),
            customer_ids: expandAllSelectionToPayloadIds({
              values: formValues.customerId,
              options: tripReportCustomerOptions,
              allOptionValue: ALL_CUSTOMERS_OPTION.value,
            }),
            export_format: formValues.exportFormat || "pdf",
            start_date: formValues.startDate || "",
            end_date: formValues.endDate || ""
          };

          tripPnlResponse = await apiService.generateTripPnlReport(payload);
        } catch (error) {
          errorNotify(error);
          return;
        }
      }

      if (selectedReportType === "vehicle-pnl-report") {
        try {
          const payload = {
            vehicle_ids: expandAllSelectionToPayloadIds({
              values: formValues.vehicleId,
              options: vehiclePnlVehicleDropdownOptions,
              allOptionValue: ALL_VEHICLES_OPTION.value,
            }),
            customer_ids: expandAllSelectionToPayloadIds({
              values: formValues.customerId,
              options: vehiclePnlCustomerDropdownOptions,
              allOptionValue: ALL_CUSTOMERS_OPTION.value,
            }),
            export_format: formValues.exportFormat || "pdf",
            start_date: formValues.startDate || "",
            end_date: formValues.endDate || "",
          };

          vehiclePnlResponse = await apiService.generateVehiclePnlReport(payload);
        } catch (error) {
          errorNotify(error);
          return;
        }
      }

      if (selectedReportType === "monthly-fleet-pnl") {
        try {
          const payload = {
            vehicle_ids: expandAllSelectionToPayloadIds({
              values: formValues.vehicleId,
              options: vehiclePnlVehicleDropdownOptions,
              allOptionValue: ALL_VEHICLES_OPTION.value,
            }),
            export_format: formValues.exportFormat || "pdf",
            month: formValues.month || "",
          };

          monthlyFleetPnlResponse = await apiService.generateMonthlyFleetPnlReport(payload);
        } catch (error) {
          errorNotify(error);
          return;
        }
      }

      const generatedTripExpenseReportData = tripExpenseResponse?.data || null;
      const generatedTripPnlReportData = tripPnlResponse?.data || null;
      const generatedVehiclePnlReportData = vehiclePnlResponse?.data || null;
      const generatedMonthlyFleetPnlReportData = monthlyFleetPnlResponse?.data || null;

      const matchedTrips = getMatchingTripsForReport(
        selectedReportType,
        formValues,
        combinedTripOptions,
        {
          vehicleOptions:
            selectedReportType === "trip-pnl-report"
              ? tripReportVehicleOptions
              : VEHICLE_FILTER_OPTIONS,
          customerOptions:
            selectedReportType === "trip-pnl-report"
              ? tripReportCustomerOptions
              : CUSTOMER_OPTIONS,
        }
      );
      const selectedTripIds = toValueArray(formValues.tripId);
      const selectedTrips = selectedTripIds
        .map((tripId) => tripLookup[String(tripId)] || null)
        .filter(Boolean);
      const selectedTrip = selectedTrips[0] || {};
      const isTripPnlAllTripsSelected =
        selectedReportType === "trip-pnl-report" &&
        safeArray(formValues.tripId).includes(ALL_TRIPS_OPTION.value);
      const selectedVehicleLabel =
        selectedReportType === "trip-expense-report"
          ? getOptionLabels(
              tripReportVehicleOptions,
              formValues.vehicleId,
              selectedTrip.vehicleId || "-"
            )
          : selectedReportType === "trip-pnl-report"
            ? getOptionLabels(
                tripPnlVehicleOptions,
                formValues.vehicleId,
                isTripPnlAllTripsSelected ? "-" : selectedTrip.vehicleId || "-"
              )
          : selectedReportType === "vehicle-pnl-report"
            ? getOptionLabels(
                vehiclePnlSelectableVehicleOptions,
                formValues.vehicleId,
                "-"
              )
          : selectedReportType === "monthly-fleet-pnl"
            ? getOptionLabels(
                vehiclePnlSelectableVehicleOptions,
                formValues.vehicleId,
                "-"
              )
          : getOptionLabel(
              VEHICLE_FILTER_OPTIONS,
              formValues.vehicleId,
              selectedTrip.vehicleId || "-"
            );
      const selectedCustomerLabel =
        selectedReportType === "trip-expense-report"
          ? getOptionLabels(
              tripReportCustomerOptions,
              tripReportCustomerOptions.map((option) => option?.value),
              selectedTrip.customer || "-"
            )
          : selectedReportType === "trip-pnl-report"
            ? getOptionLabels(
                tripPnlCustomerOptions,
                formValues.customerId,
                isTripPnlAllTripsSelected ? "-" : selectedTrip.customer || "-"
              )
            : selectedReportType === "vehicle-pnl-report"
              ? getOptionLabels(
                  vehiclePnlSelectableCustomerOptions,
                  formValues.customerId,
                  "-"
                )
            : selectedReportType === "monthly-fleet-pnl"
              ? "-"
              : getOptionLabel(CUSTOMER_OPTIONS, formValues.customerId, selectedTrip.customer || "-");
      const selectedBranchLabel =
        selectedReportType === "monthly-fleet-pnl"
          ? "-"
          : selectedTrip.branch || "-";
      const selectedDriverLabel =
        selectedReportType === "trip-expense-report"
          ? getOptionLabels(
              tripReportDriverOptions,
              formValues.driverId,
              selectedTrip.driverId || "-"
            )
          : "-";
      const selectedTripLabel =
        selectedReportType === "vehicle-pnl-report" || selectedReportType === "monthly-fleet-pnl"
          ? "-"
          : selectedReportType === "trip-expense-report"
            ? getOptionLabels(closedTripOptions, formValues.tripId, selectedTrip.value || "-")
            : selectedReportType === "trip-pnl-report"
              ? getOptionLabels(
                  tripPnLTripOptions,
                  formValues.tripId,
                  selectedTrip.value || "-"
                )
            : getOptionLabel(
                TRIP_FILTER_OPTIONS,
                formValues.tripId,
                selectedTrip.value || "-"
              );
      const tripExpenseReportView =
        selectedReportType === "trip-expense-report"
          ? getTripExpenseReportViewData({
              tripId: selectedTripLabel,
              amount: tripExpenseResponse?.data?.total_expense ?? selectedTrip.amount,
              routeLabel: selectedTrip.routeLabel,
            })
          : null;
      const nextAmount =
        tripExpenseReportView?.totalAmount ||
        matchedTrips.reduce((sum, trip) => sum + Number(trip.amount || 0), 0) ||
        Number(selectedTrip.amount || 0);
      const dateRangeLabel =
        selectedReportType === "monthly-fleet-pnl"
          ? formatMonthLabel(formValues.month)
          : `${formValues.startDate || "-"} to ${formValues.endDate || "-"}`;
      const generatedOnValue = new Date().toISOString().slice(0, 10);
      const generatedReportPayload =
        selectedReportType === "trip-expense-report"
          ? generatedTripExpenseReportData
        : selectedReportType === "trip-pnl-report"
            ? generatedTripPnlReportData
          : selectedReportType === "vehicle-pnl-report"
              ? generatedVehiclePnlReportData
          : selectedReportType === "monthly-fleet-pnl"
              ? generatedMonthlyFleetPnlReportData
              : null;
      const generatedApiRow =
        generatedReportPayload?.id
          ? {
              ...buildFinancialReportHistoryRow(
                generatedReportPayload,
                currentUser,
                userDropdownLookup
              ),
              tripId: selectedTripLabel,
              vehicleId: selectedVehicleLabel,
              customer: selectedCustomerLabel,
              branch: selectedBranchLabel,
              routeLabel: selectedTrip.routeLabel || selectedBranchLabel || "-",
              hasDetailedPreview:
                false,
            }
          : null;

      const nextRow = generatedApiRow || {
        id: `financial-report-${Date.now()}`,
        reportId: `FR-${Date.now()}`,
        reportTypeKey: selectedReportType,
        reportType: selectedReportDefinition.title || "Financial Report",
        generatedOnValue,
        generatedOnDisplay: formatHistoryDateDisplay(generatedOnValue),
        generatedBy:
          currentUser?.username || currentUser?.name || selectedDriverLabel || "AJCL Admin",
        generatedByImage: currentUser?.image || "",
        tripId: selectedTripLabel,
        vehicleId: selectedVehicleLabel,
        customer: selectedCustomerLabel,
        branch: selectedBranchLabel,
        amount:
          tripPnlResponse?.data?.net_profit ??
          vehiclePnlResponse?.data?.net_profit ??
          monthlyFleetPnlResponse?.data?.net_profit ??
          nextAmount,
        exportFormat: formValues.exportFormat,
        startDate: selectedReportType === "monthly-fleet-pnl" ? formValues.month || "" : formValues.startDate || "",
        endDate: selectedReportType === "monthly-fleet-pnl" ? "" : formValues.endDate,
        monthName: selectedReportType === "monthly-fleet-pnl" ? formatMonthLabel(formValues.month) : "-",
        dateRangeLabel,
        routeLabel:
          tripExpenseReportView?.routeLabel || selectedTrip.routeLabel || selectedBranchLabel || "-",
        expenseItems: tripExpenseReportView?.items || [],
        totalRevenue:
          tripPnlResponse?.data?.total_revenue ??
          vehiclePnlResponse?.data?.total_revenue ??
          monthlyFleetPnlResponse?.data?.total_revenue ??
          0,
        totalExpense:
          tripExpenseResponse?.data?.total_expense ??
          vehiclePnlResponse?.data?.total_expense ??
          tripPnlResponse?.data?.total_expense ??
          monthlyFleetPnlResponse?.data?.total_expense ??
          nextAmount,
        netProfit:
          tripPnlResponse?.data?.net_profit ??
          vehiclePnlResponse?.data?.net_profit ??
          monthlyFleetPnlResponse?.data?.net_profit ??
          nextAmount,
        profitMargin:
          tripPnlResponse?.data?.profit_margin ??
          vehiclePnlResponse?.data?.profit_margin ??
          monthlyFleetPnlResponse?.data?.profit_margin ??
          0,
        hasDetailedPreview: true,
      };

      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        fetchFinancialReportHistory({
          page: 1,
          pageLimit: limit,
          suppressError: true,
        });
      }

      if (selectedReportType === "trip-expense-report") {
        openTripExpenseReportModal(nextRow, true);
        return;
      }

      if (selectedReportType === "trip-pnl-report") {
        openTripPnLReportModal(nextRow, true);
        return;
      }

      if (selectedReportType === "vehicle-pnl-report") {
        openVehiclePnLReportModal(nextRow, true);
        return;
      }

      if (selectedReportType === "monthly-fleet-pnl") {
        openMonthlyFleetPnLModal(nextRow, true);
        return;
      }

      successNotify("Financial report generated successfully");
      downloadReport(nextRow);
    } finally {
      setGenerateReportLoading(false);
    }
  };

  const columns = [
    {
      name: "Report ID",
      selector: (row) => <span className="finance_link_text">{row.reportId || "-"}</span>,
      tableColumn: true,
      filterName: "reportIdFilter",
      type: "text",
      minWidth: "140px",
    },
    {
      name: "Report Type",
      selector: (row) => renderFinancialReportTypeChip(row),
      tableColumn: true,
      filterName: "reportTypeFilter",
      type: "text",
      minWidth: "210px",
    },
    {
      name: "Generated On",
      selector: (row) => row.generatedOnDisplay || row.generatedOnValue || "-",
      tableColumn: true,
      filterName: "generatedOnFilter",
      type: "date",
      minWidth: "150px",
    },
    {
      name: "Created By",
      filterLabel: "Generated By",
      selector: (row) => (
        <div className="finance_created_by_cell">
          <UserAvatar
            username={row.generatedBy}
            image={row.generatedByImage}
            marginRight="0px"
          />
          <span className="finance_created_by_name">{row.generatedBy}</span>
        </div>
      ),
      tableColumn: true,
      filterName: "generatedByFilter",
      type: "text",
      minWidth: "200px",
    },
    {
      name: "Date Range",
      selector: (row) => row.dateRangeLabel || "-",
      tableColumn: true,
      filterName: "dateRangeFilter",
      type: "text",
      minWidth: "220px",
    },
    {
      name: "Export",
      selector: (row) => String(row.exportFormat || "-").toUpperCase(),
      tableColumn: true,
      filterName: "exportFormatFilter",
      type: "text",
      minWidth: "110px",
    },
    {
      name: "Revenue (PKR)",
      selector: (row) => formatAmount(row.totalRevenue),
      tableColumn: true,
      filterName: "revenueFilter",
      type: "number",
      minWidth: "150px",
    },
    {
      name: "Expense (PKR)",
      selector: (row) => formatAmount(row.totalExpense),
      tableColumn: true,
      filterName: "expenseFilter",
      type: "number",
      minWidth: "150px",
    },
    {
      name: "Net Profit (PKR)",
      selector: (row) => formatAmount(row.netProfit),
      tableColumn: true,
      filterName: "netProfitFilter",
      type: "number",
      minWidth: "150px",
    },
    {
      name: "Margin",
      selector: (row) => `${toFiniteNumber(row.profitMargin).toFixed(2)}%`,
      tableColumn: false,
      minWidth: "110px",
    },
    {
      name: "Action",
      selector: (row) => (
        <div className="table_action_btn">
          <button
            type="button"
            className="edit_btn"
            title="Preview report"
            onClick={() => previewReport(row)}
          >
            <LuEye />
          </button>
          <button
            type="button"
            className="edit_btn"
            title="Download report"
            onClick={() => downloadReport(row)}
          >
            <LuDownload />
          </button>
        </div>
      ),
      tableColumn: false,
      minWidth: "110px",
      grow: 0,
    },
  ];

  return (
    <div className="trip_cost_estimation_page financial_reports_page">
      <Breadcrumbs values={["Finance", "Financial Reports"]} />

      <div className="access_view">
        <h2>Financial Reports</h2>
      </div>

      <p className="financial_reports_intro">
        Choose a report type, enter the required parameters, and generate report.
      </p>

      <div className="financial_reports_type_grid">
        {REPORT_TYPES.map((reportType) => {
          const ReportTypeIcon = reportType.icon;
          const isActive = selectedReportType === reportType.key;

          return (
            <button
              key={reportType.key}
              type="button"
              className={`financial_reports_type_card ${isActive ? "is_active" : ""}`}
              onClick={() => handleReportTypeChange(reportType.key)}
            >
              <span className="financial_reports_type_icon">
                {typeof ReportTypeIcon === "function" ? <ReportTypeIcon /> : ReportTypeIcon}
              </span>
              <strong>{reportType.title}</strong>
              <p>{reportType.description}</p>
            </button>
          );
        })}
      </div>

      <div className="financial_reports_card">
        <div className="financial_reports_card_header">
          <h3>Report Parameters</h3>
          <p>
            Data sourced from Expense Ledger, Fuel, Driver Expenses, Maintenance and Inventory
          </p>
        </div>

        <Row className="financial_reports_fields_row">
          {activeFormFields.map((field) => (
            <Col md={field.col || 6} key={field.name}>
              {field.component === "select" ? (
                <Select
                  label={renderFieldLabel(field.label, field.required !== false)}
                  placeholder={field.placeholder}
                  options={field.options}
                  loading={field.loading}
                  value={
                    field.isMulti
                      ? pickMultiOptions(field.options, formValues[field.name])
                      : pickOption(field.options, formValues[field.name])
                  }
                  name={field.name}
                  onChange={(option) => handleSelectFieldChange(field, option)}
                  onBlur={() => setFieldTouched(field.name)}
                  error={formErrors[field.name]}
                  touched={formTouched[field.name]}
                  isSearch={field.isSearch ?? false}
                  isMulti={field.isMulti}
                  showIcon={field.showIcon}
                  icon={field.icon}
                  isDisabled={!canCreateReports || field.isDisabled}
                />
              ) : (
                <Input
                  label={renderFieldLabel(field.label, field.required !== false)}
                  placeholder={field.placeholder}
                  type={field.inputType}
                  name={field.name}
                  value={formValues[field.name]}
                  onChange={(event) => setFieldValue(field.name, event?.target?.value || "")}
                  onBlur={() => setFieldTouched(field.name)}
                  error={formErrors[field.name]}
                  touched={formTouched[field.name]}
                  disabled={!canCreateReports}
                />
              )}
            </Col>
          ))}
        </Row>

        <div className="financial_reports_actions">
          <Button
            shouldRow={false}
            type="button"
            submitText="Generate Report"
            loadingText="Generating Report..."
            submitBtn={handleGenerateReport}
            cancelBtn={handleReset}
            cancelBtnText="Reset"
            loading={generateReportLoading}
            disabled={!isFormReady || generateReportLoading}
            cancelBtnDisabled={!isFormDirty || generateReportLoading}
            isShowSubmitBtn={canCreateReports}
          />
        </div>
      </div>

      <div className="financial_reports_card financial_reports_history_card">
        <div className="financial_reports_card_header">
          <h3>Financial Report History</h3>
        </div>

        <NewTableComponent
          showAddBtn={false}
          columns={columns}
          data={paginatedRows}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          limit={limit}
          setLimit={setLimit}
          totalItems={totalItems}
          totalPages={totalPages}
          onSearch={setSearchText}
          searchValue={searchText}
          loading={historyLoading}
          paginationLoading={historyLoading}
          setSearchFilters={setSearchFilters}
          searchFilters={searchFilters}
          limitOptions={[10, 20, 50]}
          paginationClassName="inline_pagination"
          tableClassName="invoices_table parts_table_fix financial_reports_table"
        />
      </div>

      <TripExpenseReportViewModal
        show={tripExpenseReportModal.open}
        loading={tripExpenseReportModal.loading}
        reportRow={tripExpenseReportModal.reportRow}
        reportViewData={tripExpenseReportModal.reportViewData}
        onClose={closeTripExpenseReportModal}
        onDownload={() => {
          if (tripExpenseReportModal.reportRow) {
            downloadReport(tripExpenseReportModal.reportRow);
          }
        }}
      />
      
      <TripPnLReportViewModal
        show={tripPnLReportModal.open}
        loading={tripPnLReportModal.loading}
        reportRow={tripPnLReportModal.reportRow}
        reportViewData={tripPnLReportModal.reportViewData}
        onClose={closeTripPnLReportModal}
        onDownload={() => {
          if (tripPnLReportModal.reportRow) {
            downloadReport(tripPnLReportModal.reportRow);
          }
        }}
      />
      <VehiclePnLReportViewModal
        show={vehiclePnLReportModal.open}
        loading={vehiclePnLReportModal.loading}
        reportRow={vehiclePnLReportModal.reportRow}
        reportViewData={vehiclePnLReportModal.reportViewData}
        onClose={closeVehiclePnLReportModal}
        onDownload={() => {
          if (vehiclePnLReportModal.reportRow) {
            downloadReport(vehiclePnLReportModal.reportRow);
          }
        }}
      />
      <MonthlyFleetPnLReportViewModal
        show={monthlyFleetPnLModal.open}
        loading={monthlyFleetPnLModal.loading}
        reportRow={monthlyFleetPnLModal.reportRow}
        reportViewData={monthlyFleetPnLModal.reportViewData}
        onClose={closeMonthlyFleetPnLModal}
        onDownload={() => {
          if (monthlyFleetPnLModal.reportRow) {
            downloadReport(monthlyFleetPnLModal.reportRow);
          }
        }}
      />
    </div>
  );
};

export default FinancialReports;
